import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputAdornment,
  MenuItem,
  Modal,
  Popper,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import LoadingButton from '../../../components/@extended/LoadingButton';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import useAuth from '../../../hooks/useAuth';
import { useAccountClient, useTradeClient } from '../../../client/client-hook';
import {
  AccountInfoLite,
  AccountListReply,
  AccountListReq,
  AutoMapSymbolsReply,
  AutoMapSymbolsReq,
  CompareSymbolsReply,
  CompareSymbolsReq
} from '../../../proto/account-manager_pb';
import {
  AddSlaveReply,
  AddSlavesReq,
  Config,
  CopierTemplate,
  CopyExistingTradeReq,
  GetUserConfigsReply,
  GetUserConfigsReq,
  ListCopierTemplateReply,
  ListCopierTemplateReq,
  RISK_TYPE,
  UpdateSlaveReq
} from '../../../proto/trade-copier_pb';
import { DispatchSnackbarService } from '../../../services';
import { useDispatch } from '../../../store';
import Loader from '../../../components/Loader';
import { CloseOutlined, InfoCircleFilled, WarningOutlined } from '@ant-design/icons';
import {
  CTIServerID,
  MIN_FIXED_LOT,
  MIN_RISK_SETTING,
  SeacrestMT5ServerID,
  SeacrestMTRServerID,
  sendCrispEvent
} from '../AddCopierSection';
import { alpha, useTheme } from '@mui/material/styles';
import RiskCalculatorModal from './RiskCalculatorModal';
import CalculatorSvg from 'assets/images/icons/calculator.svg';
import IconButton from '../../../components/@extended/IconButton';
import SymbolMismatchSelectionModal from './SymbolMismatchSelectionModal';
import { Alert } from '@mui/material';
import FormikEffect from 'components/custom/FormikEffect';
import { filter_type_map_value, risk_type_map_value } from '../CopyTradingTableSection';
import { Int64Value, StringValue } from 'google-protobuf/google/protobuf/wrappers_pb';
import { cloneDeep } from 'lodash';
import { ReactComponent as ChevronIcon } from '../../../assets/images/icons/chevron.svg';
import { ReactComponent as SentimentsIcon } from '../../../assets/images/icons/sidebar/sentiments.svg';
import { textSizeByBreakpoint } from '../../../utils/helper';

interface StepWrapperProps {
  children?: ReactNode;
  index: number;
  value: number;
}

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: 720,
  maxWidth: 800,
  bgcolor: 'background.paper',
  borderRadius: '8px'
};

export function StepWrapper({ children, value, index, ...other }: StepWrapperProps) {
  return (
    <div
      role="tabpanel"
      style={{ width: '100%', height: '100%' }}
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3, height: '100%' }}>{children}</Box>}
    </div>
  );
}

type Props = {
  handleClose: (hasUnsaved?: boolean) => void;
  checkHasUnsaved: (hasUnsaved: boolean) => void;
  handleSuccess: () => void;
  selectedConfigId: string;
  masterAccountId: string;
  getFormState: (state: number) => void;
};

const service = new DispatchSnackbarService();

const AddSlaveForm = ({ handleClose, handleSuccess, selectedConfigId, getFormState, masterAccountId, checkHasUnsaved }: Props) => {
  const [apiCalling, setApiCalling] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingCompare, setLoadingCompare] = useState(false);

  const [accountList, setAccountList] = useState<AccountInfoLite.AsObject[]>([]);
  const [configList, setConfigList] = useState<Config.AsObject[]>([]);
  const [accountListCopyTo, setAccountListCopyTo] = useState<AccountInfoLite.AsObject[]>([]);
  const [selectedCopyTo, setSelectedCopyTo] = useState<any>(null);
  const [copyFromList, setCopyFromList] = useState<AccountInfoLite.AsObject[]>([]);
  const { user } = useAuth();
  const accountClient = useAccountClient();
  const tradeClient = useTradeClient();
  const dispatch = useDispatch();
  const [symbolSuggestions, setSymbolSuggestions] = useState<string[]>([]);
  const [selectedSlaveId, setSelectedSlaveId] = useState('');
  const [selectedMasterId, setSelectedMasterId] = useState('');
  const [selectedSlaveAccountId, setSelectedSlaveAccountId] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const theme = useTheme();

  const accordionStyle = {
    border: 'none',
    backgroundColor: theme.palette.background.default,
    borderRadius: '20px',
    overflow: 'hidden',
    paddingX: { xl: 1, lg: 0.75, md: 0.5, sm: 0.4, xs: 0.3 },
    paddingY: { xl: 1.25, lg: 1, md: 0.75, sm: 0.6, xs: 0.5 }
  };

  const accordionSummaryStyle = {
    backgroundColor: 'transparent',
    '& .MuiAccordionSummary-expandIconWrapper': {
      order: 1,
      transition: 'transform 0.3s ease'
    },
    '& .MuiAccordionSummary-content': {
      order: 2,
      marginLeft: 0,
      display: 'flex',
      alignItems: 'center',
      gap: 1
    },
    '&.Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
      transform: 'rotate(-180deg)'
    }
  };

  const accordionDetailsStyle = {
    border: 'none',
    paddingTop: 1,
    '& .MuiGrid-container': {
      marginTop: 0
    }
  };

  const inputStyle = {
    backgroundColor: theme.palette.background.paper,
    borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.divider,
    fontSize: textSizeByBreakpoint(16),
    borderRadius: '6px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '6px',
      height: '48px',
      '& fieldset': {
        borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.divider,
        borderRadius: '6px'
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main
      }
    },
    '& .MuiInputBase-input': {
      padding: '12.5px 14px',
      height: '23px',
      lineHeight: '23px'
    }
  };

  const selectStyle = {
    backgroundColor: theme.palette.background.paper,
    borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.divider,
    fontSize: textSizeByBreakpoint(16),
    borderRadius: '6px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '6px',
      height: '48px',
      '& fieldset': {
        borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.divider,
        borderRadius: '6px'
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main
      }
    },
    '& .MuiSelect-select': {
      padding: '12.5px 14px',
      height: '23px',
      lineHeight: '23px'
    },
    '& .MuiInputLabel-root': {
      fontSize: textSizeByBreakpoint(14)
    },
    '& .MuiPopover-root': {
      zIndex: 9999
    },
    '& .MuiMenu-paper': {
      zIndex: 9999
    }
  };

  const checkboxStyle = {
    color: theme.palette.primary.main,
    '&.Mui-checked': {
      color: theme.palette.primary.main
    },
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.primary.main, 0.04),
      borderRadius: 2
    }
  };

  const [openRiskCalculator, setOpenRiskCalculator] = useState(false);
  const [copyExpanded, setCopyExpanded] = useState(true);
  const [advanceExpanded, setAdvanceExpanded] = useState(false);
  const [filterExpanded, setFilterExpanded] = useState(false);
  const [slTpExpand, setSlTpExpand] = useState(false);
  const [isCTIAccount, setIsCTIAccount] = useState(false);
  const [isSeacrest, setIsSeacrest] = useState(false);
  const [masterServer, setMasterServer] = useState('');
  const [copyEx, setCopyEx] = useState(false);

  const handleAddSlave = (values: any) => {
    console.log(values);

    if (
      user &&
      user?.tcId &&
      values?.copyTo &&
      values?.riskType &&
      !invalidTVMaster(values) &&
      (parseInt(values?.riskType) === RISK_TYPE.FIXED_BALANCE_MULTIPLIER ? values?.fixedMasterBalance > 0.01 : true) &&
      (parseInt(values?.riskType) === RISK_TYPE.BALANCE_PERCENTAGE
        ? values?.riskSettingsBalancePercentage > 0 && values?.riskSettingsBalancePercentage <= 100
        : true) &&
      (parseInt(values?.riskType) !== RISK_TYPE.BALANCE_PERCENTAGE
        ? values?.riskSettings > MIN_RISK_SETTING || values?.riskSettingsFixedLot > MIN_FIXED_LOT
        : true) &&
      (values?.copyMagicNumber === 'Custom' ? values?.magicNumber <= 2147483647 && values?.magicNumber >= 1 : true) &&
      ((values?.magicFilter === 'COPY' || values?.magicFilter === 'IGNORE') && values?.magicFilterValue
        ? values?.magicFilterValue <= 2147483647 && values?.magicFilterValue >= 1
        : true) &&
      (values?.filterByLotSize === 'Yes'
        ? values?.filterByLotSizeMinValue >= 0.01 &&
          values?.filterByLotSizeMaxValue &&
          values?.filterByLotSizeMinValue <= values?.filterByLotSizeMaxValue
        : true) &&
      (values.delayType === 'random'
        ? !!values?.tradeDelayMin && !!values.tradeDelayMax && +values?.tradeDelayMin <= +values?.tradeDelayMax
        : values?.delayType === 'fixed'
        ? !!values?.tradeDelayMin
        : true) &&
      (values?.fixedStopLoss === 'Yes' ? values?.fixedStopLossValue > 0 : true) &&
      (values?.fixedTakeProfit === 'Yes' ? values?.fixedTakeProfitValue > 0 : true) &&
      (values?.stopLossRefinement === 'Yes' ? values?.stopLossRefinementValue != 0 : true) &&
      (values?.takeProfitRefinement === 'Yes' ? values?.takeProfitRefinementValue != 0 : true)
    ) {
      addSlave(values);
    } else if (invalidTVMaster(values)) {
      service.dispatchSnackbar(dispatch, invalidTVMaster(values) as any, 'alert', 'error', 5000);
    } else if (values?.copyMagicNumber === 'Custom' && values?.magicNumber < 1) {
      service.dispatchSnackbar(dispatch, 'Custom Magic number range is 1 to 2147483647', 'alert', 'error', 5000);
    } else {
      // Check for Fixed SL/TP validation errors
      const slError = values?.fixedStopLoss === 'Yes' && !(values?.fixedStopLossValue > 0);
      const tpError = values?.fixedTakeProfit === 'Yes' && !(values?.fixedTakeProfitValue > 0);
      
      if (slError && tpError) {
        service.dispatchSnackbar(dispatch, 'Fixed SL and Fixed TP values required', 'alert', 'error', 5000);
      } else if (slError) {
        service.dispatchSnackbar(dispatch, 'Fixed SL value required', 'alert', 'error', 5000);
      } else if (tpError) {
        service.dispatchSnackbar(dispatch, 'Fixed TP value required', 'alert', 'error', 5000);
      } else {
        service.dispatchSnackbar(dispatch, 'Invalid input', 'alert', 'error', 5000);
      }
    }
  };

  const invalidTVMaster = (values: any) => {
    if (hasTVAccount()) {
      if (values.copyPendingOrders) {
        return 'Copy pending order not allowed for Tradovate';
      }
    }
    if (hasTVAccount()) {
      if (values.copyMagicNumber && (values.copyMagicNumber !== 'No' || +values?.magicNumber !== 0)) {
        return 'Copy magic number not allowed for Tradovate';
      }
      if (getMagicFilters(values) && getMagicFilters(values).length > 0) {
        return 'Magic filters not allowed for Tradovate';
      }
      if (getFilterComments(values) && getFilterComments(values).length > 0) {
        return 'Comment filters not allowed for Tradovate';
      }
    }
    return null;
  };

  useEffect(() => {
    getFormState(activeStep);
  }, [activeStep]);

  const isComment = (selectedCopyTo: string) => {
    const copyTo = accountList.find((e) => e.accountId === selectedCopyTo);
    if (!copyTo) {
      return false;
    }

    return copyTo?.platform === 'MT-4' || copyTo?.platform === 'MT-5' || copyTo?.platform === 'CTR';
  };

  const addSlave = (values: any) => {
    setApiCalling(true);
    tradeClient()
      .then((client) => {
        const slaveReq: AddSlavesReq.AsObject = {
          configId: selectedConfigId,
          userId: user?.tcId || '',
          slaveAccountsList: [values?.copyTo]
        };
        client
          .addSlave(slaveReq)
          .then((res) => {
            const slaveResponse = res as AddSlaveReply.AsObject;
            console.log(slaveResponse);
            getConfig(values);
          })
          .catch((err) => {
            console.log(err);
            setApiCalling(false);
            const msz = err.message === 'no permission to access this RPC' ? 'Permission denied' : err.message;
            service.dispatchSnackbar(dispatch, msz, 'alert', 'error', 5000);
          });
      })
      .catch((err) => {
        console.log('ERROR: ', err);
        setApiCalling(false);
        const msz = err.message === 'no permission to access this RPC' ? 'Permission denied' : err.message;
        service.dispatchSnackbar(dispatch, msz, 'alert', 'error', 5000);
      });
  };

  const getConfig = (values: any, readOnly = false) => {
    tradeClient()
      .then((client) => {
        const req: GetUserConfigsReq.AsObject = {
          userId: user?.tcId || ''
        };
        client
          .getUserConfigs(req)
          .then((res: any) => {
            const config = res as GetUserConfigsReply.AsObject;
            console.log('getUserConfigs: ', res);
            if (readOnly) {
              setConfigList(config.configsList);
              return;
            }
            const slave = config?.configsList
              ?.find((e) => e.id === selectedConfigId)
              ?.slavesList?.find((x) => x.accountId === values?.copyTo);
            const master = config?.configsList?.find((e) => e.id === selectedConfigId)?.master;
            const masterId = master?.accountId;
            const slaveAccountId = config?.configsList
              ?.find((e) => e.id === selectedConfigId)
              ?.slavesList?.find((x) => x.accountId === values?.copyTo)?.accountId;
            console.log(selectedConfigId, slave?.id, masterId);
            const slaveAccountNumber = slave?.accountNumber;
            const masterAccountNumber = master?.accountNumber;
            updateSlave(values, slave?.id, slaveAccountId, masterId, slaveAccountNumber, masterAccountNumber);
          })
          .catch((err) => {
            console.log('ERROR: ', err);
            const msz = err.message === 'no permission to access this RPC' ? 'Permission denied' : err.message;
            service.dispatchSnackbar(dispatch, msz, 'alert', 'error', 5000);
          });
      })
      .catch((err) => {
        console.log('ERROR: ', err);
        const msz = err.message === 'no permission to access this RPC' ? 'Permission denied' : err.message;
        service.dispatchSnackbar(dispatch, msz, 'alert', 'error', 5000);
      });
  };

  const onAutoMapping = async (slaveId: string, slaveAccId: string, masterAccId: string, pattern: string) => {
    if (user && user?.tcId && slaveId && slaveAccId && masterAccId) {
      setLoadingCompare(true);
      try {
        const client = await accountClient();
        const req: AutoMapSymbolsReq.AsObject = {
          userId: user?.tcId || '',
          slaveId: slaveId,
          masterAccount: masterAccId,
          slaveAccount: slaveAccId,
          pattern: pattern,
          overwriteExisting: false
        };
        const res = await client.autoMapSymbols(req);
        const result = res as AutoMapSymbolsReply.AsObject;
        console.log('AutoMapSymbolsReply: ', result);
        service.dispatchSnackbar(dispatch, 'Slave added successfully.', 'alert', 'success', 5000);
        handleSuccess();
        handleClose();
      } catch (err: any) {
        console.log('ERROR: ', err);
        const msz = err.message === 'no permission to access this RPC' ? 'Permission denied' : err.message;
        service.dispatchSnackbar(dispatch, msz, 'alert', 'error', 5000);
      } finally {
        setLoadingCompare(false);
      }
    }
  };

  const compareSymbol = (masterId: any, slaveAccountId: any, slaveId: any, copyExisting: boolean) => {
    if (user && user?.tcId) {
      setLoadingCompare(true);
      accountClient()
        .then((client) => {
          const req: CompareSymbolsReq.AsObject = {
            userId: user?.tcId || '',
            masterAccount: masterId || null,
            slaveAccount: slaveAccountId || null
          };
          client
            .compareSymbol(req)
            .then(async (res) => {
              console.log(res);
              const result = res as CompareSymbolsReply.AsObject;
              // const test: string[] = ['EXAMPLE', 'EXAMPLE.i'];
              // const test: string[] = [];
              // result.suggestionsList = test;
              if (result?.suggestionsList?.length <= 0) {
                await onAutoMapping(slaveId, slaveAccountId, masterId, '');
                if (copyExisting) {
                  await copyExistingTrade(slaveId, masterId, slaveAccountId);
                }
                setLoadingCompare(false);
              } else {
                result.suggestionsList?.sort((a, b) => {
                  if (a?.toLowerCase() === 'example' && b?.toLowerCase() !== 'example') {
                    return 1; // 'EXAMPLE' should come after everything else
                  } else if (a?.toLowerCase() !== 'example' && b?.toLowerCase() === 'example') {
                    return -1; // 'EXAMPLE' should come before everything else
                  } else {
                    return 0; // Maintain the original order of other elements
                  }
                });
                setSelectedMasterId(masterId);
                setSelectedSlaveId(slaveId);
                setSelectedSlaveAccountId(slaveAccountId);
                setSymbolSuggestions(result.suggestionsList);
                setLoadingCompare(false);
                if (result?.suggestionsList?.length === 1) {
                  await onAutoMapping(slaveId, slaveAccountId, masterId, result.suggestionsList[0]);
                  if (copyExisting) {
                    await copyExistingTrade(slaveId, masterId, slaveAccountId);
                  }
                }
              }
            })
            .catch((err) => {
              setLoadingCompare(false);
              console.log('ERROR: ', err);
            });
        })
        .catch((err) => {
          setLoadingCompare(false);
          console.log('ERROR: ', err);
        });
    }
  };

  const updateSlave = (
    values: any,
    slaveId: any,
    slaveAccountId: any,
    masterId: any,
    slaveAccountNumber?: string,
    masterAccountNumber?: string
  ) => {
    tradeClient()
      .then((client) => {
        const riskType = values.riskType;
        console.log(values);
        const updateSlaveReq: UpdateSlaveReq.AsObject = {
          userId: user?.tcId || '',
          configId: selectedConfigId,
          slaveId: slaveId,
          riskType: values?.riskType,
          lotMultiplierValue: {
            value: riskType === RISK_TYPE.LOT_MULTIPLIER ? values?.riskSettings : 0
          },
          fixedLotValue: {
            value: riskType === RISK_TYPE.FIXED_LOT ? values?.riskSettingsFixedLot : 0
          },
          balanceMultiplierValue: {
            value: riskType === RISK_TYPE.BALANCE_MULTIPLIER ? values?.riskSettings : 0
          },
          balancePercentageValue: {
            value: riskType === RISK_TYPE.BALANCE_PERCENTAGE ? values.riskSettingsBalancePercentage : 0
          },
          enable: { value: true },
          copyTakeProfit: { value: values?.copyTakeProfit },
          copyStopLoss: { value: values?.copyStopLoss },
          copyPendingOrders: { value: values?.copyPendingOrders },
          forceMinimumLotSize: { value: values?.forceMinimumLotSize },
          forceMaximumLotSize: { value: values?.forceMaximumLotSize },
          copyComment: { value: values?.copyComment !== 'No' },
          customComment: { value: values?.customComment },
          reverseTrade: { value: values?.reverseTrade },
          strictClose: { value: values?.strictClose },
          considerContractSize: { value: values?.considerContractSize },
          copyMagicNumber: { value: values?.copyMagicNumber !== 'No' },
          magicNumber: { value: values?.copyMagicNumber !== 'Custom' ? 0 : values?.magicNumber },
          lotRefinerValue: { value: values?.lotRefiner || 0 },
          magicFilterType: filter_type_map_value[values.magicFilter],
          commentFilterType: filter_type_map_value[values.filterByComment],
          magicFiltersList: getMagicFilters(values),
          copyExpiryTime: { value: values?.copyExpiryTime },
          commentFiltersList: getFilterComments(values),
          minLotFilter: {
            value: values?.filterByLotSize === 'Yes' && values?.filterByLotSizeMinValue ? values?.filterByLotSizeMinValue : null
          },
          maxLotFilter: {
            value: values?.filterByLotSize === 'Yes' && values?.filterByLotSizeMaxValue ? values?.filterByLotSizeMaxValue : null
          },
          stopLossPip: {
            value: values?.fixedStopLoss === 'Yes' && values?.fixedStopLossValue ? values?.fixedStopLossValue : 0
          },
          takeProfitPip: {
            value: values?.fixedTakeProfit === 'Yes' && values?.fixedTakeProfitValue ? values?.fixedTakeProfitValue : 0
          },
          slRefiner: {
            value: values?.stopLossRefinement === 'Yes' && values?.stopLossRefinementValue ? values?.stopLossRefinementValue : 0
          },
          tpRefiner: {
            value: values?.takeProfitRefinement === 'Yes' && values?.takeProfitRefinementValue ? values?.takeProfitRefinementValue : 0
          },
          copyExistingTrades: {
            value: values?.copyExistingTrades
          },
          allowedTradeSide: {
            value: values?.directionType === 'ALL' ? null : values?.directionType
          },
          tradingHoursList: []
        };
        if (values.delayType === 'random') {
          if (!!values?.tradeDelayMin || values?.tradeDelayMin == 0) {
            updateSlaveReq.minDelayTime = { value: +values.tradeDelayMin };
          }
          if (!!values?.tradeDelayMax || values?.tradeDelayMax == 0) {
            updateSlaveReq.maxDelayTime = { value: +values.tradeDelayMax };
          }
        } else if (values.delayType === 'fixed') {
          if (!!values?.tradeDelayMin || values?.tradeDelayMin == 0) {
            updateSlaveReq.minDelayTime = { value: +values.tradeDelayMin };
            updateSlaveReq.maxDelayTime = { value: +values.tradeDelayMin };
          }
        }
        if (riskType === RISK_TYPE.FIXED_BALANCE_MULTIPLIER) {
          updateSlaveReq.fixedBalanceMultiplierValue = {
            value: riskType === RISK_TYPE.FIXED_BALANCE_MULTIPLIER ? values?.riskSettings : 0
          };
          updateSlaveReq.fixedMasterBalance = {
            value: values?.fixedMasterBalance
          };
        }
        console.log('updateSlaveReq: ', updateSlaveReq);
        client
          .updateSlave(updateSlaveReq)
          .then((res) => {
            console.log(res);
            setActiveStep(1);
            setCopyEx(values?.copyExistingTrades);
            compareSymbol(masterId, slaveAccountId, slaveId, values?.copyExistingTrades);
            sendCrispEvent(updateSlaveReq, client, 'Slave Config Added', slaveAccountNumber, masterAccountNumber);
          })
          .catch((err) => {
            console.log(err);
            setApiCalling(false);
            const msz = err.message === 'no permission to access this RPC' ? 'Permission denied' : err.message;
            service.dispatchSnackbar(dispatch, msz, 'alert', 'error', 5000);
          });
      })
      .catch((err) => {
        console.log('ERROR: ', err);
        setApiCalling(false);
        const msz = err.message === 'no permission to access this RPC' ? 'Permission denied' : err.message;
        service.dispatchSnackbar(dispatch, msz, 'alert', 'error', 5000);
      });
  };

  const copyExistingTrade = async (slaveId: string, masterAccountId: string, slaveAccountId: string) => {
    if (user && user?.tcId && slaveId && masterAccountId && slaveAccountId) {
      try {
        setLoading(true);
        const client = await tradeClient();
        const req: CopyExistingTradeReq.AsObject = {
          userId: user?.tcId || '',
          slaveId: slaveId,
          masterAccountId: masterAccountId
        };
        const res = await client.copyExistingTrade(req);
        console.log(res);
        setLoading(false);
      } catch (err: any) {
        console.log('ERROR: ', err);
        setLoading(false);
      }
    }
  };

  const [selectedMagicNumbers, setSelectedMagicNumbers] = useState<string[]>([]);
  const [selectedComments, setSelectedComments] = useState<string[]>([]);
  const [currentMagicNumber, setCurrentMagicNumber] = useState('');
  const [currentComment, setCurrentComment] = useState('');

  const getMagicFilters = (values: any) => {
    const magicFilters: Int64Value.AsObject[] =
      (values?.magicFilter === 'COPY' || values?.magicFilter === 'IGNORE') && selectedMagicNumbers
        ? selectedMagicNumbers
            .map((num: string) => parseFloat(num))
            .filter((num: number) => !isNaN(num))
            .map((num: number) => ({
              value: num
            }))
        : [];
    return magicFilters;
  };

  const getFilterComments = (values: any) => {
    const comments: StringValue.AsObject[] =
      (values?.filterByComment === 'COPY' || values?.filterByComment === 'IGNORE') && selectedComments
        ? selectedComments
            .filter((comment: string) => !!comment)
            .map((comment: string) => ({
              value: comment
            }))
        : [];
    return comments;
  };

  const isMTRMaster = () => {
    if (copyFromList && copyFromList[0]) {
      const platform = copyFromList[0].platform;
      return platform === 'MTR';
    }

    return false;
  };

  const isTLMaster = () => {
    if (copyFromList && copyFromList[0]) {
      const platform = copyFromList[0].platform;
      return platform === 'TL';
    }

    return false;
  };

  const hasTVAccount = () => {
    let hasTV = false;
    if (copyFromList && copyFromList[0]) {
      const platform = copyFromList[0].platform;
      if (platform && platform === 'TV') {
        hasTV = true;
      }
    }
    if (selectedCopyTo) {
      let tmpAccounts = cloneDeep(accountList);
      const copyTo = tmpAccounts.find((e) => e.accountId === selectedCopyTo);
      if (copyTo && copyTo?.platform === 'TV') {
        hasTV = true;
      }
    }

    return hasTV;
  };

  const hasTVAccountDirect = (sCopyTo: string) => {
    let hasTV = false;
    if (copyFromList && copyFromList[0]) {
      const platform = copyFromList[0].platform;
      if (platform && platform === 'TV') {
        hasTV = true;
      }
    }
    if (sCopyTo) {
      let tmpAccounts = cloneDeep(accountList);
      const copyTo = tmpAccounts.find((e) => e.accountId === sCopyTo);
      if (copyTo && copyTo?.platform === 'TV') {
        hasTV = true;
      }
    }

    return hasTV;
  };

  useEffect(() => {
    if (accountList.length > 0 && configList.length > 0) {
      const currentConfig = configList?.find((e) => e.id === selectedConfigId);
      const prevSlaves = currentConfig?.slavesList.map((e) => e.accountId);
      console.log('prevSlaves: ', prevSlaves);
      let tmpAllowedSlaves: AccountInfoLite.AsObject[] = [];
      accountList?.forEach((x) => {
        const exists = prevSlaves?.includes(x.accountId);
        if (currentConfig?.master?.accountId === x.accountId) {
          setCopyFromList([x]);
        } else if (!exists && currentConfig?.master?.accountId !== x.accountId) {
          tmpAllowedSlaves.push(x);
        }
      });
      console.log('tmpAllowedSlaves: ', tmpAllowedSlaves);
      setAccountListCopyTo(tmpAllowedSlaves);
      const masterServerId = accountList.find((x) => x.accountId === masterAccountId)?.serverId;
      setMasterServer(masterServerId || '');
      if (masterServerId === CTIServerID) {
        setIsCTIAccount(true);
      }
      if (masterServerId === SeacrestMT5ServerID || masterServerId === SeacrestMTRServerID) {
        setIsSeacrest(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountList, configList]);

  useEffect(() => {
    if (user && user?.tcId) {
      setLoading(true);
      const fetchingAccount = async () => {
        await fetchUserAccounts();
      };
      fetchingAccount();
      getConfig(null, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUserAccounts = async (cursor = '') => {
    try {
      const client = await accountClient();
      const req: AccountListReq.AsObject = {
        userId: user?.tcId || '',
        pageSize: 50,
        paginationCursor: cursor,
        query: ''
      };
      const res = await client.getAccountList(req);
      const result = res as AccountListReply.AsObject;
      setAccountList((prev) => [...prev, ...result.accountsList]);
      setLoading(false);
      if (result?.accountsList?.length === 50) {
        await fetchUserAccounts(result.nextPageCursor);
      }
    } catch (err) {
      console.log('ERROR: ', err);
      setLoading(false);
    }
  };

  const initialValues = React.useMemo(
    () => ({
      template: '',
      copyFrom: copyFromList[0]?.accountId,
      copyTo: '',
      riskType: '',
      directionType: 'ALL',
      riskSettings: '',
      riskSettingsFixedLot: '',
      riskSettingsBalancePercentage: '',
      copyTakeProfit: false,
      copyStopLoss: false,
      copyPendingOrders: false,
      copyExistingTrades: false,
      forceMinimumLotSize: true,
      forceMaximumLotSize: false,
      reverseTrade: false,
      copyComment: 'No',
      customComment: '',
      strictClose: false,
      considerContractSize: false,
      copyMagicNumber: 'No',
      magicNumber: '',
      fixedMasterBalance: '',
      lotRefiner: '0',
      delayType: 'no',
      tradeDelayMin: '',
      tradeDelayMax: '',
      magicFilter: 'UNKNOWN',
      magicFilterValue: '',
      copyExpiryTime: true,
      filterByComment: 'UNKNOWN',
      filterByCommentValue: '',
      filterByLotSize: '',
      filterByLotSizeMinValue: '',
      filterByLotSizeMaxValue: '',
      fixedStopLoss: 'No',
      fixedStopLossValue: '',
      fixedTakeProfit: 'No',
      fixedTakeProfitValue: '',
      stopLossRefinement: 'No',
      stopLossRefinementValue: '',
      takeProfitRefinement: 'No',
      takeProfitRefinementValue: '',
      submit: null
    }),
    [copyFromList]
  );

  useEffect(() => {
    setFormValues({ ...initialValues });
  }, [initialValues]);

  const getDelayType = (dto: CopierTemplate.AsObject | null): string => {
    if (dto?.minDelayTime?.value && dto?.minDelayTime?.value !== dto?.maxDelayTime?.value) {
      return 'random';
    } else if (dto?.minDelayTime?.value && dto?.maxDelayTime?.value && dto?.minDelayTime?.value === dto?.maxDelayTime?.value) {
      return 'fixed';
    } else {
      return 'no';
    }
  };

  const [templates, setTemplates] = useState<CopierTemplate.AsObject[]>([]);
  const [currentTemplate, setCurrentTemplate] = useState<CopierTemplate.AsObject | null>(null);
  const getTemplates = async () => {
    if (user && user?.tcId) {
      setLoading(true);
      try {
        const client = await tradeClient();
        const req: ListCopierTemplateReq.AsObject = {
          userId: user?.tcId || ''
        };
        const res = await client.listCopierTemplate(req);
        console.log('ListCopierTemplateReply: ', res);
        const result = res as ListCopierTemplateReply.AsObject;
        setTemplates(result.copierTemplatesList);
        setLoading(false);
      } catch (err: any) {
        console.log('ERROR: ', err);
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    getTemplates();
  }, []);

  const [formValues, setFormValues] = useState(initialValues);
  const formikRef = useRef<FormikProps<any>>(null);
  useEffect(() => {
    if (formikRef && currentTemplate) {
      formikRef.current?.setFieldValue('riskType', risk_type_map_value[currentTemplate?.riskType?.value as any] || '');
      formikRef.current?.setFieldValue('directionType', currentTemplate?.allowedTradeSide?.value || 'ALL');
      formikRef.current?.setFieldValue(
        'riskSettings',
        currentTemplate?.riskType?.value === 'FIXED_BALANCE_MULTIPLIER'
          ? currentTemplate?.fixedBalanceMultiplierValue?.value
          : currentTemplate?.riskType?.value === 'LOT_MULTIPLIER'
          ? currentTemplate?.lotMultiplierValue?.value
          : currentTemplate?.riskType?.value === 'BALANCE_PERCENTAGE'
          ? currentTemplate?.balanceMultiplierValue?.value
          : currentTemplate?.riskType?.value === 'BALANCE_MULTIPLIER'
          ? currentTemplate?.balanceMultiplierValue?.value
          : ''
      );
      formikRef.current?.setFieldValue('riskSettingsFixedLot', currentTemplate?.fixedLotValue?.value);
      formikRef.current?.setFieldValue('riskSettingsBalancePercentage', currentTemplate?.balancePercentageValue?.value || '');
      formikRef.current?.setFieldValue('copyTakeProfit', currentTemplate?.copyTakeProfit?.value || false);
      formikRef.current?.setFieldValue('copyStopLoss', currentTemplate?.copyStopLoss?.value || false);
      formikRef.current?.setFieldValue('copyPendingOrders', currentTemplate?.copyPendingOrders?.value || false);
      formikRef.current?.setFieldValue('forceMinimumLotSize', currentTemplate?.forceMinimumLotSize?.value || false);
      formikRef.current?.setFieldValue('forceMaximumLotSize', currentTemplate?.forceMaximumLotSize?.value || false);
      formikRef.current?.setFieldValue('customPlacedType', currentTemplate?.customPlacedType?.value || 'No');
      formikRef.current?.setFieldValue('strictClose', currentTemplate?.strictClose?.value || false);
      formikRef.current?.setFieldValue('considerContractSize', currentTemplate?.considerContractSize?.value || false);
      formikRef.current?.setFieldValue('magicNumber', currentTemplate?.magicNumber?.value || '');
      formikRef.current?.setFieldValue(
        'fixedMasterBalance',
        currentTemplate?.riskType?.value === 'FIXED_BALANCE_MULTIPLIER' ? currentTemplate?.fixedMasterBalance?.value : ''
      );
      formikRef.current?.setFieldValue('lotRefiner', currentTemplate?.lotRefinerValue?.value || '');
      formikRef.current?.setFieldValue(
        'magicFilter',
        currentTemplate?.magicFilterType?.value ? currentTemplate?.magicFilterType.value : 'UNKNOWN'
      );
      formikRef.current?.setFieldValue('magicFilterValue', ''); // TODO: need check
      formikRef.current?.setFieldValue('copyExpiryTime', currentTemplate?.copyExpiryTime?.value || false);
      formikRef.current?.setFieldValue(
        'filterByComment',
        currentTemplate?.commentFilterType?.value ? currentTemplate?.commentFilterType.value : 'UNKNOWN'
      );
      formikRef.current?.setFieldValue('filterByCommentValue', ''); // TODO: need check
      formikRef.current?.setFieldValue(
        'filterByLotSize',
        currentTemplate?.minLotFilter?.value && currentTemplate?.maxLotFilter?.value ? 'Yes' : 'No'
      );
      formikRef.current?.setFieldValue('filterByLotSizeMinValue', currentTemplate?.minLotFilter?.value);
      formikRef.current?.setFieldValue('filterByLotSizeMaxValue', currentTemplate?.maxLotFilter?.value);
      formikRef.current?.setFieldValue(
        'fixedStopLoss',
        !!currentTemplate?.stopLossPip?.value && currentTemplate?.stopLossPip?.value > 0 ? 'Yes' : 'No'
      );
      formikRef.current?.setFieldValue('fixedStopLossValue', currentTemplate?.stopLossPip?.value);
      formikRef.current?.setFieldValue(
        'fixedTakeProfit',
        !!currentTemplate?.takeProfitPip?.value && currentTemplate?.takeProfitPip?.value > 0 ? 'Yes' : 'No'
      );
      formikRef.current?.setFieldValue('fixedTakeProfitValue', currentTemplate?.takeProfitPip?.value);
      formikRef.current?.setFieldValue(
        'stopLossRefinement',
        !!currentTemplate?.slRefiner?.value && currentTemplate?.slRefiner?.value != 0 ? 'Yes' : 'No'
      );
      formikRef.current?.setFieldValue('stopLossRefinementValue', currentTemplate?.slRefiner?.value);
      formikRef.current?.setFieldValue(
        'takeProfitRefinement',
        !!currentTemplate?.tpRefiner?.value && currentTemplate?.tpRefiner?.value != 0 ? 'Yes' : 'No'
      );
      formikRef.current?.setFieldValue('takeProfitRefinementValue', currentTemplate?.tpRefiner?.value);

      if (!isCTIAccount && !isSeacrest) {
        formikRef.current?.setFieldValue(
          'copyMagicNumber',
          currentTemplate?.copyMagicNumber?.value &&
            currentTemplate?.magicNumber?.value &&
            currentTemplate?.magicNumber?.value?.toString() !== '0'
            ? 'Custom'
            : currentTemplate?.copyMagicNumber?.value
            ? 'Yes'
            : 'No'
        );
        formikRef.current?.setFieldValue('delayType', getDelayType(currentTemplate));
        formikRef.current?.setFieldValue('tradeDelayMin', currentTemplate?.minDelayTime?.value || '');
        formikRef.current?.setFieldValue('tradeDelayMax', currentTemplate?.maxDelayTime?.value || '');
        formikRef.current?.setFieldValue('reverseTrade', currentTemplate?.reverseTrade?.value || false);
      } else {
        formikRef.current?.setFieldValue('copyMagicNumber', 'Yes');
        formikRef.current?.setFieldValue('delayType', 'no');
        formikRef.current?.setFieldValue('tradeDelayMin', '');
        formikRef.current?.setFieldValue('tradeDelayMax', '');
        formikRef.current?.setFieldValue('reverseTrade', false);
      }

      if (!isSeacrest) {
        formikRef.current?.setFieldValue(
          'copyComment',
          currentTemplate?.copyComment?.value && currentTemplate?.customComment?.value
            ? 'Custom'
            : currentTemplate?.copyComment?.value
            ? 'Yes'
            : 'No'
        );
        formikRef.current?.setFieldValue('customComment', currentTemplate?.customComment?.value || '');
      } else {
        formikRef.current?.setFieldValue('copyComment', 'Yes');
        formikRef.current?.setFieldValue('customComment', '');
      }

      // Reset values that violate platform conditions
      if (hasTVAccount()) {
        // TV accounts don't allow copyPendingOrders
        if (currentTemplate?.copyPendingOrders?.value) {
          formikRef.current?.setFieldValue('copyPendingOrders', false);
        }
        // TV accounts don't allow magic number copying
        if (currentTemplate?.copyMagicNumber?.value) {
          formikRef.current?.setFieldValue('copyMagicNumber', 'No');
        }
        // TV accounts don't allow custom magic numbers
        if (currentTemplate?.magicNumber?.value && currentTemplate?.magicNumber?.value !== 0) {
          formikRef.current?.setFieldValue('magicNumber', '');
        }
        // TV accounts don't allow magic filters
        if (
          (currentTemplate?.magicFiltersList && currentTemplate?.magicFiltersList.length > 0) ||
          (getMagicFilters(currentTemplate) && getMagicFilters(currentTemplate).length > 0)
        ) {
          formikRef.current?.setFieldValue('magicFilter', 'UNKNOWN');
          formikRef.current?.setFieldValue('magicFilterValue', '');
          setSelectedMagicNumbers([]);
        }
        // TV accounts don't allow comment filters
        if (
          (currentTemplate?.commentFiltersList && currentTemplate?.commentFiltersList.length > 0) ||
          (getMagicFilters(currentTemplate) && getMagicFilters(currentTemplate).length > 0)
        ) {
          formikRef.current?.setFieldValue('filterByComment', 'UNKNOWN');
          formikRef.current?.setFieldValue('filterByCommentValue', '');
          setSelectedComments([]);
        }
      }

      if (isTLMaster()) {
        // TL Master doesn't allow Fixed % risk
        if (parseInt(currentTemplate?.riskType?.value || '0') === RISK_TYPE.BALANCE_PERCENTAGE) {
          formikRef.current?.setFieldValue('riskType', RISK_TYPE.BALANCE_MULTIPLIER.toString());
          formikRef.current?.setFieldValue('riskSettings', '');
          formikRef.current?.setFieldValue('riskSettingsBalancePercentage', '');
        }
      }

      if (isMTRMaster()) {
        // MTR Master doesn't allow copyPendingOrders
        if (currentTemplate?.copyPendingOrders?.value) {
          formikRef.current?.setFieldValue('copyPendingOrders', false);
        }
      }

      // Reset custom magic numbers for CTI and Seacrest (they should use 'Yes', not 'Custom')
      if ((isCTIAccount || isSeacrest) && currentTemplate?.magicNumber?.value && currentTemplate?.magicNumber?.value !== 0) {
        formikRef.current?.setFieldValue('magicNumber', '');
      }
    } else {
      if (formikRef.current) {
        formikRef.current.setValues({
          ...initialValues,
          copyFrom: formikRef.current.values.copyFrom,
          copyTo: formikRef.current.values.copyTo
        });
      }
    }
  }, [currentTemplate]);

  useEffect(() => {
    if (currentTemplate) {
      if (currentTemplate && currentTemplate?.magicFiltersList?.length > 0) {
        setSelectedMagicNumbers(currentTemplate?.magicFiltersList?.map((e) => e.value?.toString()));
      }
      if (currentTemplate && currentTemplate?.commentFiltersList?.length > 0) {
        setSelectedComments(currentTemplate?.commentFiltersList?.map((e) => e.value));
      }
    }
  }, [currentTemplate]);

  const [copyTemplateWarning, setCopyTemplateWarning] = useState<null | string>('');

  useEffect(() => {
    const ctiWarning = validateCTIServer(currentTemplate);
    const seacrestWarning = validateSeacrestServer(currentTemplate);
    const tvWarning = validateTVAccount(currentTemplate);
    const tlWarning = validateTLMaster(currentTemplate);
    const mtrWarning = validateMTRMaster(currentTemplate);

    const warnings = [ctiWarning, seacrestWarning, tvWarning, tlWarning, mtrWarning].filter(Boolean);

    if (warnings.length > 0) {
      setCopyTemplateWarning(warnings.join('; '));
    } else {
      setCopyTemplateWarning('');
    }
  }, [currentTemplate, isCTIAccount, isSeacrest]);

  const validateCTIServer = (template: CopierTemplate.AsObject | null): string | null => {
    if (!isCTIAccount || !template) return null;
    const errors: string[] = [];

    if (template.reverseTrade?.value) {
      errors.push('ReverseTrade is not allowed for CTI');
    }

    if (getDelayType(template) !== 'no') {
      errors.push('DelayType is not allowed for CTI');
    }

    if (!template.copyMagicNumber?.value) {
      errors.push('Must copy magic number for CTI');
    }

    if (template.copyMagicNumber?.value && template.magicNumber?.value && template.magicNumber?.value !== 0) {
      errors.push('Custom Magic Number is not allowed for CTI (must use Yes, not Custom)');
    }

    return errors.length > 0 ? errors.join(', ') : null;
  };

  const validateSeacrestServer = (template: CopierTemplate.AsObject | null) => {
    if (!isSeacrest || !template) return null;
    const errors: string[] = [];

    if (template.reverseTrade?.value) {
      errors.push('ReverseTrade is not allowed for Seacrest');
    }

    if (getDelayType(template) !== 'no') {
      errors.push('DelayType is not allowed for Seacrest');
    }

    if (!template.copyMagicNumber?.value) {
      errors.push('Must copy magic number for Seacrest');
    }

    if (!template.copyComment?.value || !template.customComment?.value) {
      errors.push('Must copy default comment for Seacrest');
    }

    if (template.copyMagicNumber?.value && template.magicNumber?.value && template.magicNumber?.value !== 0) {
      errors.push('Custom Magic Number is not allowed for Seacrest (must use Yes, not Custom)');
    }

    return errors.length > 0 ? errors.join(', ') : null;
  };

  const validateTVAccount = (template: CopierTemplate.AsObject | null): string | null => {
    if (!hasTVAccount() || !template) return null;
    const errors: string[] = [];

    if (template.copyPendingOrders?.value) {
      errors.push('Copy Pending Orders is not allowed for Tradovate');
    }

    if (template.copyMagicNumber?.value) {
      errors.push('Copy Magic Number is not allowed for Tradovate');
    }

    if (template.magicNumber?.value && template.magicNumber?.value !== 0) {
      errors.push('Custom Magic Number is not allowed for Tradovate');
    }

    if (template.magicFiltersList && template.magicFiltersList.length > 0) {
      errors.push('Magic Filters are not allowed for Tradovate');
    }

    if (template.commentFiltersList && template.commentFiltersList.length > 0) {
      errors.push('Comment Filters are not allowed for Tradovate');
    }

    return errors.length > 0 ? errors.join(', ') : null;
  };

  const validateTLMaster = (template: CopierTemplate.AsObject | null): string | null => {
    if (!isTLMaster() || !template) return null;
    const errors: string[] = [];

    if (parseInt(template.riskType?.value || '0') === RISK_TYPE.BALANCE_PERCENTAGE) {
      errors.push('Fixed % risk is not allowed for TL Master');
    }

    return errors.length > 0 ? errors.join(', ') : null;
  };

  const validateMTRMaster = (template: CopierTemplate.AsObject | null): string | null => {
    if (!isMTRMaster() || !template) return null;
    const errors: string[] = [];

    if (template.copyPendingOrders?.value) {
      errors.push('Copy Pending Orders is not allowed for MTR Master');
    }

    return errors.length > 0 ? errors.join(', ') : null;
  };

  const hasUnsavedChanges = (initialValues: any, values: any) => {
    const changes = Object.keys(initialValues)
      .filter((k) => k !== 'copyFrom')
      .some((key) => JSON.stringify(initialValues[key]) !== JSON.stringify(values[key]));
    return changes;
  };

  useEffect(() => {
    if (checkHasUnsaved) {
      const hasChanges = hasUnsavedChanges(initialValues, formValues);
      checkHasUnsaved(hasChanges);
    }
  }, [formValues, initialValues]);

  return (
    <>
      {(loading || loadingCompare) && <Loader />}
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Box display="flex" flexDirection="column" gap={1}>
            <Grid item>
              <Grid item xs={12} sm={12} md={12} lg={12} display={'flex'} sx={{ m: 2, height: '50px' }} justifyContent={'space-between'}>
                <Grid></Grid>
                <Grid item xs={12} md={8} xl={8} sx={{ margin: 'auto' }}>
                  <Grid item xs={12}>
                    <Stepper nonLinear activeStep={activeStep}>
                      {['Configure trade copier', 'Verifying copier settings'].map((label, index) => (
                        <Step key={label}>
                          <StepLabel
                            color="inherit"
                            /*onClick={() => {
                          if (activeStep === 0) {
                            setActiveStep(1);
                            compareSymbol(
                              '3a4bad04-0d05-4585-ada8-f84ce3498a32',
                              '3a4bad04-0d05-4585-ada8-f84ce3498a23',
                              '8b910156-b17e-4b02-860a-b66bcaa79071'
                            );
                          }
                        }}*/
                            sx={{ '& .MuiStepLabel-label': { fontSize: '0.875rem', fontWeight: 'bold' } }}
                          >
                            {activeStep === 0 && label === 'Verifying copier settings'
                              ? 'Verify copier settings'
                              : activeStep === 1 && symbolSuggestions?.length > 0 && label === 'Verifying copier settings'
                              ? 'Mismatch detected - Slave'
                              : label}
                          </StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  </Grid>
                </Grid>
                <Grid item sx={{ height: '100%' }}>
                  <Grid item sx={{ lineHeight: '50px' }}>
                    {!(selectedSlaveId && selectedMasterId && selectedSlaveAccountId && symbolSuggestions?.length > 0 && !loadingCompare) &&
                      !loadingCompare && (
                        <IconButton
                          onClick={() => {
                            if (activeStep === 1) {
                              service.dispatchSnackbar(dispatch, 'Slave added successfully', 'alert', 'success', 5000);
                              handleSuccess();
                              handleClose();
                            } else {
                              handleClose(hasUnsavedChanges(initialValues, formValues));
                            }
                          }}
                        >
                          <CloseOutlined />
                        </IconButton>
                      )}
                  </Grid>
                </Grid>
              </Grid>
              <Divider sx={{ width: '100%', borderTopWidth: '1px' }} />
            </Grid>
            <>
              <StepWrapper value={activeStep} index={0}>
                <Grid item xs={12}>
                  <Formik
                    innerRef={formikRef}
                    initialValues={initialValues}
                    enableReinitialize={true}
                    validationSchema={Yup.object().shape({
                      copyTo: Yup.string().max(255).required('Copy to is required'),
                      riskType: Yup.string().max(255).required('Risk type is required'),
                      riskSettingsFixedLot: Yup.number()
                        .moreThan(MIN_FIXED_LOT, 'Risk settings must be greater than ' + MIN_FIXED_LOT)
                        .required('Risk settings value is required a valid number'),
                      riskSettingsBalancePercentage: Yup.number()
                        .moreThan(0, 'Risk settings min value grater than 0')
                        .max(100, 'Risk settings max value is 100')
                        .required('Risk settings value is required a valid number'),
                      riskSettings: Yup.number()
                        .moreThan(MIN_RISK_SETTING, 'Risk settings must be greater than ' + MIN_RISK_SETTING)
                        .required('Risk settings value is required a valid number'),
                      fixedMasterBalance: Yup.number().min(0.01, 'Fixed master balance min value 0.01').required('Valid number required'),
                      customComment: Yup.string().max(25, 'Maximum length of custom comment is 25'),
                      magicNumber: Yup.number()
                        .max(2147483647, 'Magic number range is 1 to 2147483647')
                        .min(0, 'Magic number range is 1 to 2147483647')
                        .notOneOf([0], 'For 0 please set copy to No'),
                      tradeDelayMin: Yup.number().min(0, 'Trade delay must be positive').integer('Trade delay must be integer'),
                      tradeDelayMax: Yup.number()
                        .min(0, 'Trade delay must be positive')
                        .integer('Trade delay must be integer')
                        .test('maxMinDelayCheck', 'Max value must be greater than or equal to Min value', (value: any, context: any) => {
                          const minValue = context.parent.tradeDelayMin;
                          console.log('minValue: ', minValue);
                          if (!value) {
                            return true;
                          }
                          return value >= minValue;
                        }),
                      magicFilterValue: Yup.number()
                        .max(2147483647, 'Magic filter range is 1 to 2147483647')
                        .min(0, 'Magic filter range is 1 to 2147483647')
                        .notOneOf([0], 'For 0 please set magic filter to No'),
                      filterByLotSizeMinValue: Yup.number().min(0.01, 'Minimum lot size value is 0.01'),
                      filterByLotSizeMaxValue: Yup.number()
                        .min(0.02, 'Minimum lot size value is 0.02')
                        .test('maxMinCheck', 'Max value must be greater than or equal to Min value', (value: any, context: any) => {
                          const filterByLotSizeMinValue = context.parent.filterByLotSizeMinValue;
                          const filterByLotSize = context.parent.filterByLotSize;
                          if (filterByLotSize !== 'Yes') {
                            return true;
                          }
                          console.log('filterByLotSizeMinValue: ', filterByLotSizeMinValue);
                          return value >= filterByLotSizeMinValue;
                        }),
                      fixedStopLossValue: Yup.number().when('fixedStopLoss', {
                        is: (fixedStopLoss: any) => fixedStopLoss === 'Yes',
                        then: Yup.number()
                          .required('Fixed stop loss is required')
                          .min(0, 'Fixed stop loss must be positive')
                          .notOneOf([0], 'For 0 please set fixed stop loss to No'),
                        otherwise: Yup.number().nullable()
                      }),
                      fixedTakeProfitValue: Yup.number().when('fixedTakeProfit', {
                        is: (fixedTakeProfit: any) => fixedTakeProfit === 'Yes',
                        then: Yup.number()
                          .required('Fixed take profit is required')
                          .min(0, 'Fixed take profit must be positive')
                          .notOneOf([0], 'For 0 please set fixed take profit to No'),
                        otherwise: Yup.number().nullable()
                      }),
                      stopLossRefinementValue: Yup.number().when('stopLossRefinement', {
                        is: (stopLossRefinement: any) => stopLossRefinement === 'Yes',
                        then: Yup.number()
                          .required('Stop loss refinement is required')
                          // .min(0, 'Stop loss refinement must be positive')
                          .notOneOf([0], 'For 0 please set stop loss refinement to No'),
                        otherwise: Yup.number().nullable()
                      }),
                      takeProfitRefinementValue: Yup.number().when('takeProfitRefinement', {
                        is: (takeProfitRefinement: any) => takeProfitRefinement === 'Yes',
                        then: Yup.number()
                          .required('Take profit refinement is required')
                          // .min(0, 'Take profit refinement must be positive')
                          .notOneOf([0], 'For 0 please set take profit refinement to No'),
                        otherwise: Yup.number().nullable()
                      })
                    })}
                    onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {}}
                  >
                    {({ errors, handleBlur, handleChange, setFieldValue, handleSubmit, isSubmitting, touched, values }) => (
                      <form noValidate onSubmit={handleSubmit}>
                        <FormikEffect values={values} onChange={(values) => setFormValues(values)} />
                        <Grid container spacing={3} sx={{ maxWidth: 920 }}>
                          {copyTemplateWarning && (
                            <Grid item xs={12} sx={{ paddingTop: '0px !important' }}>
                              <Alert color="warning" icon={<WarningOutlined />} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                {copyTemplateWarning}
                              </Alert>
                            </Grid>
                          )}
                          {parseInt(values.riskType) === RISK_TYPE.BALANCE_PERCENTAGE && (
                            <Grid item xs={12} sx={{ paddingTop: '0px !important' }}>
                              <Alert color="warning" icon={<InfoCircleFilled />}>
                                Balance percentage risk requires your master trades to contain a Stop Loss, without this trades will fail to
                                copy.
                              </Alert>
                            </Grid>
                          )}
                          <Grid item xs={12} sx={{ minHeight: '260px' }}>
                            <Grid item xs={12} style={{ paddingTop: '12px' }}>
                              <Accordion defaultExpanded sx={accordionStyle} expanded={copyExpanded}>
                                <AccordionSummary
                                  sx={accordionSummaryStyle}
                                  expandIcon={<ChevronIcon />}
                                  onClick={(e) => {
                                    if (!copyExpanded) {
                                      setCopyExpanded(true);
                                      setSlTpExpand(false);
                                      setFilterExpanded(false);
                                      setAdvanceExpanded(false);
                                    }
                                  }}
                                >
                                  <Box
                                    component={SentimentsIcon}
                                    sx={{
                                      width: { xs: 16, sm: 17, md: 18, lg: 19, xl: 20 },
                                      height: { xs: 16, sm: 17, md: 18, lg: 19, xl: 20 },
                                      color: theme.palette.primary.main
                                    }}
                                  />
                                  <Typography component="span" color="textPrimary" fontSize={16} fontWeight="600" lineHeight="100%">
                                    Risk Settings
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={accordionDetailsStyle}>
                                  <Grid container spacing={3}>
                                    <Grid item xs={12} display={'flex'}>
                                      <Grid item xs={12}>
                                        <Typography
                                          variant="subtitle2"
                                          fontWeight={500}
                                          mb={1}
                                          color="textSecondary"
                                          fontSize={16}
                                          lineHeight="100%"
                                        >
                                          Template
                                        </Typography>
                                        <Stack>
                                          <Autocomplete
                                            fullWidth
                                            id="template"
                                            onChange={(event, value: any) => {
                                              console.log('setCurrentTemplate: ', value);
                                              setCurrentTemplate(value);
                                            }}
                                            onBlur={(event) => {
                                              handleBlur(event);
                                            }}
                                            onOpen={handleBlur}
                                            options={templates}
                                            getOptionLabel={(option) => `${option.name}`}
                                            isOptionEqualToValue={(option: any, value: any) => option?.value?.id === value?.value?.id}
                                            PopperComponent={(props) => (
                                              <Popper
                                                {...props}
                                                style={{
                                                  zIndex: 999999,
                                                  position: 'relative'
                                                }}
                                                placement="bottom-start"
                                              />
                                            )}
                                            renderInput={(params) => (
                                              <TextField {...params} error={Boolean(touched.template && errors.template)} sx={inputStyle} />
                                            )}
                                          />
                                          {touched.template && errors.template && (
                                            <FormHelperText error id="helper-text-template">
                                              {errors.template}
                                            </FormHelperText>
                                          )}
                                        </Stack>
                                      </Grid>
                                    </Grid>

                                    <Grid item xs={12} display={'flex'}>
                                      <Grid sx={{ mr: 2 }} item xs={12}>
                                        <Typography
                                          variant="subtitle2"
                                          fontWeight={500}
                                          mb={1}
                                          color="textSecondary"
                                          fontSize={16}
                                          lineHeight="100%"
                                        >
                                          Copy From Account
                                        </Typography>
                                        <Stack>
                                          <Autocomplete
                                            fullWidth
                                            disablePortal
                                            disabled={true}
                                            id="copy-from"
                                            onBlur={(event) => {
                                              touched.copyFrom = true;
                                              handleBlur(event);
                                            }}
                                            value={copyFromList[0] ? copyFromList[0] : null}
                                            options={copyFromList}
                                            onOpen={handleBlur}
                                            getOptionLabel={(option) => `${option.nickname} (${option.accountNumber})`}
                                            isOptionEqualToValue={(option: any, value: any) =>
                                              option?.value?.accountId === value?.value?.accountId
                                            }
                                            renderInput={(params) => (
                                              <TextField {...params} error={Boolean(touched.copyFrom && errors.copyFrom)} sx={inputStyle} />
                                            )}
                                          />
                                        </Stack>
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Typography
                                          variant="subtitle2"
                                          fontWeight={500}
                                          mb={1}
                                          color="textSecondary"
                                          fontSize={16}
                                          lineHeight="100%"
                                        >
                                          Copy To Account
                                        </Typography>
                                        <Stack>
                                          <Autocomplete
                                            fullWidth
                                            id="copy-to"
                                            onChange={(event, value: any) => {
                                              if (value === null) {
                                                setSelectedCopyTo(null);
                                                setFieldValue('copyTo', '');
                                                return;
                                              }
                                              if (hasTVAccountDirect(value.accountId)) {
                                                setFieldValue('copyPendingOrders', false);
                                                setFieldValue('copyMagicNumber', 'No');
                                                setFieldValue('magicNumber', '');
                                                setSelectedMagicNumbers([]);
                                                setSelectedComments([]);
                                                console.log('lotrefiner => ', values, values?.lotRefiner, values?.filterByLotSizeMaxValue);
                                                if (values?.lotRefiner && values?.lotRefiner?.toString()?.includes('.')) {
                                                  setFieldValue('lotRefiner', Math.round(parseFloat(values?.lotRefiner))?.toString());
                                                }
                                                if (
                                                  values?.filterByLotSizeMaxValue &&
                                                  values?.filterByLotSizeMaxValue?.toString()?.includes('.')
                                                ) {
                                                  setFieldValue(
                                                    'filterByLotSizeMaxValue',
                                                    Math.round(parseFloat(values?.filterByLotSizeMaxValue))?.toString()
                                                  );
                                                }
                                                if (
                                                  values?.filterByLotSizeMinValue &&
                                                  values?.filterByLotSizeMinValue?.toString()?.includes('.')
                                                ) {
                                                  setFieldValue(
                                                    'filterByLotSizeMinValue',
                                                    Math.round(parseFloat(values?.filterByLotSizeMinValue))?.toString()
                                                  );
                                                }
                                                if (
                                                  parseInt(values.riskType) === RISK_TYPE.FIXED_LOT &&
                                                  values?.riskSettingsFixedLot &&
                                                  values?.riskSettingsFixedLot?.toString()?.includes('.')
                                                ) {
                                                  setFieldValue(
                                                    'riskSettingsFixedLot',
                                                    Math.round(parseFloat(values?.riskSettingsFixedLot))?.toString()
                                                  );
                                                }
                                              }
                                              setSelectedCopyTo(value?.accountId);
                                              setFieldValue('copyTo', value?.accountId);
                                              const copyTo = accountList.find((e) => e.accountId === value?.accountId);
                                              console.log('copyTo: ', copyTo);
                                              setIsCTIAccount(value?.serverId === CTIServerID || masterServer === CTIServerID);
                                              setIsSeacrest(
                                                value?.serverId === SeacrestMT5ServerID ||
                                                  masterServer === SeacrestMT5ServerID ||
                                                  value?.serverId === SeacrestMTRServerID ||
                                                  masterServer === SeacrestMTRServerID
                                              );
                                              console.log(
                                                'copy-to: ',
                                                value?.serverId === CTIServerID || masterServer === CTIServerID,
                                                value?.serverId === SeacrestMT5ServerID || masterServer === SeacrestMT5ServerID
                                              );

                                              if (value?.serverId === CTIServerID || masterServer === CTIServerID) {
                                                setFieldValue('reverseTrade', false);
                                                setFieldValue('delayType', 'no');
                                                setFieldValue('tradeDelayMin', '');
                                                setFieldValue('tradeDelayMax', '');
                                                setFieldValue('copyMagicNumber', 'Yes');
                                              } else if (
                                                value?.serverId === SeacrestMT5ServerID ||
                                                masterServer === SeacrestMT5ServerID ||
                                                value?.serverId === SeacrestMTRServerID ||
                                                masterServer === SeacrestMTRServerID
                                              ) {
                                                setFieldValue('reverseTrade', false);
                                                setFieldValue('delayType', 'no');
                                                setFieldValue('tradeDelayMin', '');
                                                setFieldValue('tradeDelayMax', '');
                                                setFieldValue('copyComment', 'Yes');
                                                setFieldValue('customComment', '');
                                                setFieldValue('copyMagicNumber', 'Yes');
                                              } else {
                                                setFieldValue('copyMagicNumber', 'No');
                                                setFieldValue('copyComment', 'No');
                                              }
                                            }}
                                            onBlur={(event) => {
                                              touched.copyTo = true;
                                              handleBlur(event);
                                            }}
                                            onOpen={handleBlur}
                                            options={accountListCopyTo}
                                            getOptionLabel={(option) => `${option.nickname} (${option.accountNumber})`}
                                            isOptionEqualToValue={(option: any, value: any) =>
                                              option?.value?.accountId === value?.value?.accountId
                                            }
                                            PopperComponent={(props) => (
                                              <Popper
                                                {...props}
                                                style={{
                                                  zIndex: 999999,
                                                  position: 'relative'
                                                }}
                                                placement="bottom-start"
                                              />
                                            )}
                                            renderInput={(params) => (
                                              <TextField {...params} error={Boolean(touched.copyTo && errors.copyTo)} sx={inputStyle} />
                                            )}
                                          />
                                          {touched.copyTo && errors.copyTo && (
                                            <FormHelperText error id="helper-text-copy-to">
                                              {errors.copyTo}
                                            </FormHelperText>
                                          )}
                                        </Stack>
                                      </Grid>
                                    </Grid>

                                    <Grid item xs={12} display={'flex'}>
                                      <Grid sx={{ mr: 2 }} item xs={12}>
                                        <Stack spacing={1}>
                                          <Typography
                                            variant="subtitle2"
                                            fontWeight={500}
                                            mb={1}
                                            color="textSecondary"
                                            fontSize={16}
                                            lineHeight="100%"
                                          >
                                            Risk Type
                                          </Typography>
                                          <FormControl fullWidth error={Boolean(touched.riskType && errors.riskType)}>
                                            <Select
                                              id="risk-type"
                                              name="riskType"
                                              value={values.riskType}
                                              onBlur={handleBlur}
                                              MenuProps={{
                                                PaperProps: {
                                                  sx: {
                                                    zIndex: 9999,
                                                    maxHeight: 300,
                                                    borderRadius: '6px'
                                                  }
                                                },
                                                sx: {
                                                  zIndex: 9999
                                                }
                                              }}
                                              sx={selectStyle}
                                              onChange={(event, value) => {
                                                console.log(event?.target?.value);
                                                if (
                                                  +event?.target?.value === RISK_TYPE.FIXED_BALANCE_MULTIPLIER ||
                                                  +event?.target?.value === RISK_TYPE.BALANCE_MULTIPLIER
                                                ) {
                                                  setFieldValue('considerContractSize', true);
                                                } else {
                                                  setFieldValue('considerContractSize', false);
                                                }
                                                setFieldValue('riskType', event?.target?.value);
                                                setFieldValue('riskType', event?.target?.value);
                                                setFieldValue('riskSettingsFixedLot', '');
                                                setFieldValue('riskSettings', '');
                                                setFieldValue('fixedMasterBalance', '');
                                              }}
                                              error={Boolean(touched.riskType && errors.riskType)}
                                            >
                                              <MenuItem value={RISK_TYPE.FIXED_LOT}>Fixed Lot</MenuItem>
                                              <MenuItem value={RISK_TYPE.LOT_MULTIPLIER}>Lot Multiplier</MenuItem>
                                              <MenuItem value={RISK_TYPE.BALANCE_MULTIPLIER}>Balance Multiplier</MenuItem>
                                              <MenuItem value={RISK_TYPE.FIXED_BALANCE_MULTIPLIER}>Fixed Balance Multiplier</MenuItem>
                                              {!isTLMaster() && <MenuItem value={RISK_TYPE.BALANCE_PERCENTAGE}>Fixed % risk</MenuItem>}
                                            </Select>
                                            {touched.riskType && errors.riskType && (
                                              <FormHelperText error id="helper-text-risk-type">
                                                {errors.riskType}
                                              </FormHelperText>
                                            )}
                                          </FormControl>
                                        </Stack>
                                      </Grid>
                                      <Grid item xs={12}>
                                        {parseInt(values.riskType) === RISK_TYPE.FIXED_BALANCE_MULTIPLIER && (
                                          <Stack spacing={1}>
                                            <Typography
                                              variant="subtitle2"
                                              fontWeight={500}
                                              mb={1}
                                              color="textSecondary"
                                              fontSize={16}
                                              lineHeight="100%"
                                            >
                                              Fixed Master Balance
                                            </Typography>
                                            <TextField
                                              variant="outlined"
                                              fullWidth
                                              id="fixedMasterBalance"
                                              name="fixedMasterBalance"
                                              type="number"
                                              placeholder="0.00"
                                              value={values.fixedMasterBalance}
                                              onChange={handleChange}
                                              onBlur={handleBlur}
                                              error={Boolean(touched.fixedMasterBalance && errors.fixedMasterBalance)}
                                              helperText={touched.fixedMasterBalance && errors.fixedMasterBalance}
                                              sx={inputStyle}
                                              InputProps={{
                                                endAdornment: <InputAdornment position="end">$</InputAdornment>
                                              }}
                                            />
                                          </Stack>
                                        )}
                                        {(parseInt(values.riskType) === RISK_TYPE.LOT_MULTIPLIER ||
                                          parseInt(values.riskType) === RISK_TYPE.BALANCE_MULTIPLIER ||
                                          parseInt(values.riskType) === RISK_TYPE.FIXED_BALANCE_MULTIPLIER ||
                                          values.riskType === '') && (
                                          <Stack
                                            spacing={1}
                                            sx={{ mt: parseInt(values.riskType) === RISK_TYPE.FIXED_BALANCE_MULTIPLIER ? 2 : 0 }}
                                          >
                                            <Typography
                                              variant="subtitle2"
                                              fontWeight={500}
                                              mb={1}
                                              color="textSecondary"
                                              fontSize={16}
                                              lineHeight="100%"
                                            >
                                              Risk Setting - 100% = 1 to 1 risk
                                            </Typography>
                                            <TextField
                                              variant="outlined"
                                              fullWidth
                                              id="risk-settings"
                                              name="riskSettings"
                                              type="number"
                                              placeholder="0"
                                              value={values.riskSettings}
                                              onChange={handleChange}
                                              onBlur={handleBlur}
                                              error={Boolean(touched.riskSettings && errors.riskSettings)}
                                              helperText={touched.riskSettings && errors.riskSettings}
                                              sx={inputStyle}
                                              InputProps={{
                                                endAdornment: <InputAdornment position="end">%</InputAdornment>
                                              }}
                                            />
                                          </Stack>
                                        )}
                                        {parseInt(values.riskType) === RISK_TYPE.FIXED_LOT && (
                                          <Stack spacing={1}>
                                            <Typography
                                              variant="subtitle2"
                                              fontWeight={500}
                                              mb={1}
                                              color="textSecondary"
                                              fontSize={16}
                                              lineHeight="100%"
                                            >
                                              Lot Size
                                            </Typography>
                                            <TextField
                                              variant="outlined"
                                              fullWidth
                                              id="risk-settings-fixed-lot"
                                              name="riskSettingsFixedLot"
                                              type="number"
                                              placeholder="0.00"
                                              value={values.riskSettingsFixedLot}
                                              onChange={(e) => {
                                                if (hasTVAccount()) {
                                                  const value = e.target.value;
                                                  if (value.includes('.')) {
                                                    // If decimal is entered, round it to nearest integer
                                                    e.target.value = Math.round(parseFloat(value)).toString();
                                                  }
                                                }
                                                handleChange(e);
                                              }}
                                              onBlur={handleBlur}
                                              error={Boolean(touched.riskSettingsFixedLot && errors.riskSettingsFixedLot)}
                                              helperText={touched.riskSettingsFixedLot && errors.riskSettingsFixedLot}
                                              sx={inputStyle}
                                              inputProps={{
                                                step: hasTVAccount() ? '1' : '0.01'
                                              }}
                                            />
                                          </Stack>
                                        )}
                                        {parseInt(values.riskType) === RISK_TYPE.BALANCE_PERCENTAGE && (
                                          <Stack spacing={1}>
                                            <Typography
                                              variant="subtitle2"
                                              fontWeight={500}
                                              mb={1}
                                              color="textSecondary"
                                              fontSize={16}
                                              lineHeight="100%"
                                            >
                                              Balance percentage
                                            </Typography>
                                            <TextField
                                              variant="outlined"
                                              fullWidth
                                              id="risk-settings-BALANCE_PERCENTAGE"
                                              name="riskSettingsBalancePercentage"
                                              type="number"
                                              placeholder="Balance percentage"
                                              value={values.riskSettingsBalancePercentage}
                                              onChange={handleChange}
                                              onBlur={handleBlur}
                                              error={Boolean(touched.riskSettingsBalancePercentage && errors.riskSettingsBalancePercentage)}
                                              helperText={touched.riskSettingsBalancePercentage && errors.riskSettingsBalancePercentage}
                                              sx={inputStyle}
                                            />
                                          </Stack>
                                        )}
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </AccordionDetails>
                              </Accordion>
                            </Grid>

                            <Grid item xs={12} style={{ paddingTop: '12px' }}>
                              <Accordion expanded={slTpExpand} sx={accordionStyle}>
                                <AccordionSummary
                                  expandIcon={
                                    <ChevronIcon
                                      style={{
                                        width: 16,
                                        height: 16,
                                        transform: slTpExpand ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.2s'
                                      }}
                                    />
                                  }
                                  onClick={(e) => {
                                    if (!slTpExpand) {
                                      setCopyExpanded(false);
                                      setSlTpExpand(true);
                                      setFilterExpanded(false);
                                      setAdvanceExpanded(false);
                                    }
                                  }}
                                  sx={accordionSummaryStyle}
                                >
                                  <SentimentsIcon style={{ width: 16, height: 16, marginRight: 8 }} />
                                  <Typography
                                    variant="body1"
                                    component="span"
                                    color="textPrimary"
                                    fontWeight="600"
                                    sx={{ fontSize: textSizeByBreakpoint(16) }}
                                  >
                                    SL and TP Settings
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={accordionDetailsStyle}>
                                  <Grid item xs={12} style={{ paddingTop: '12px' }} display={'flex'}>
                                    <Grid item xs={4}>
                                      <FormControlLabel
                                        value="start"
                                        control={
                                          <Checkbox
                                            name="copyStopLoss"
                                            disabled={values?.strictClose}
                                            checked={values.copyStopLoss}
                                            sx={checkboxStyle}
                                            onChange={(event, checked) => {
                                              if (!checked) {
                                                setFieldValue('stopLossRefinement', 'No');
                                                setFieldValue('stopLossRefinementValue', '');
                                              } else {
                                                setFieldValue('fixedStopLoss', 'No');
                                                setFieldValue('fixedStopLossValue', '');
                                              }
                                              setFieldValue('copyStopLoss', checked);
                                            }}
                                          />
                                        }
                                        label="Copy Stop Loss"
                                        labelPlacement="end"
                                        sx={{ ml: 1, mr: 1 }}
                                      />
                                    </Grid>
                                    <Grid item xs={4}>
                                      <FormControlLabel
                                        value="start"
                                        control={
                                          <Checkbox
                                            name="copyTakeProfit"
                                            disabled={values?.strictClose}
                                            checked={values.copyTakeProfit}
                                            sx={checkboxStyle}
                                            onChange={(event, checked) => {
                                              setFieldValue('copyTakeProfit', checked);
                                              if (!checked) {
                                                setFieldValue('takeProfitRefinement', 'No');
                                                setFieldValue('takeProfitRefinementValue', '');
                                              } else {
                                                setFieldValue('fixedTakeProfit', 'No');
                                                setFieldValue('fixedTakeProfitValue', '');
                                              }
                                            }}
                                          />
                                        }
                                        label="Copy Take Profit"
                                        labelPlacement="end"
                                        sx={{ ml: 1, mr: 1 }}
                                      />
                                    </Grid>
                                    <Grid item xs={4}>
                                      {(isMTRMaster() || hasTVAccount()) && (
                                        <Tooltip
                                          title={
                                            <Typography
                                              color={
                                                theme.palette.mode === 'dark'
                                                  ? theme.palette.background.paper
                                                  : theme.typography.caption.color
                                              }
                                              variant={'caption'}
                                            >
                                              {!hasTVAccount()
                                                ? 'Not currently available for MatchTrader masters, trades will execute when the master order fills'
                                                : 'Not currently available for Tradovate, trades will execute when the master order fills'}
                                            </Typography>
                                          }
                                        >
                                          <FormControlLabel
                                            value="start"
                                            control={
                                              <Checkbox
                                                name="copyPendingOrders"
                                                disabled={values.reverseTrade || isMTRMaster() || hasTVAccount()}
                                                checked={values.copyPendingOrders}
                                                sx={checkboxStyle}
                                                onChange={handleChange}
                                              />
                                            }
                                            label="Copy Pending Orders"
                                            labelPlacement="end"
                                            sx={{ ml: 1, mr: 1 }}
                                          />
                                        </Tooltip>
                                      )}
                                      {!(isMTRMaster() || hasTVAccount()) && (
                                        <FormControlLabel
                                          value="start"
                                          control={
                                            <Checkbox
                                              name="copyPendingOrders"
                                              disabled={values.reverseTrade || isMTRMaster()}
                                              checked={values.copyPendingOrders}
                                              sx={checkboxStyle}
                                              onChange={handleChange}
                                            />
                                          }
                                          label="Copy Pending Orders"
                                          labelPlacement="end"
                                          sx={{ ml: 1, mr: 1 }}
                                        />
                                      )}
                                    </Grid>
                                  </Grid>
                                  <Grid item xs={12} sx={{ mt: 3 }} justifyContent={'space-between'} display={'flex'}>
                                    <Grid sx={{ mr: 2 }} item xs={6}>
                                      <Stack spacing={1}>
                                        <Tooltip
                                          title={
                                            !values?.copyStopLoss ? (
                                              ''
                                            ) : (
                                              <Typography
                                                color={
                                                  theme.palette.mode === 'dark'
                                                    ? theme.palette.background.paper
                                                    : theme.typography.caption.color
                                                }
                                                variant={'caption'}
                                              >
                                                Remove copy stop loss first
                                              </Typography>
                                            )
                                          }
                                        >
                                          <Box>
                                            <Typography
                                              variant="subtitle2"
                                              fontWeight={500}
                                              mb={1}
                                              color="textSecondary"
                                              fontSize={16}
                                              lineHeight="100%"
                                            >
                                              Fixed Stop Loss (pips)
                                            </Typography>
                                            <FormControl fullWidth error={Boolean(touched.fixedStopLoss && errors.fixedStopLoss)}>
                                              <Select
                                                id="fixedStopLoss"
                                                name="fixedStopLoss"
                                                value={values.fixedStopLoss}
                                                onBlur={handleBlur}
                                                MenuProps={{
                                                  PaperProps: {
                                                    sx: {
                                                      zIndex: 9999,
                                                      maxHeight: 300,
                                                      borderRadius: '6px'
                                                    }
                                                  },
                                                  sx: {
                                                    zIndex: 9999
                                                  }
                                                }}
                                                sx={selectStyle}
                                                onChange={(e, v) => {
                                                  setFieldValue('fixedStopLoss', e?.target?.value);
                                                  setFieldValue('fixedStopLossValue', '');
                                                  if (e?.target?.value === 'Yes' && values.copyStopLoss && values.strictClose) {
                                                    setFieldValue('copyStopLoss', false);
                                                  } else if (e?.target?.value === 'No' && !values.copyStopLoss && values.strictClose) {
                                                    setFieldValue('copyStopLoss', true);
                                                  }
                                                }}
                                                disabled={values.copyStopLoss && !values.strictClose}
                                                error={Boolean(touched.fixedStopLoss && errors.fixedStopLoss)}
                                              >
                                                <MenuItem value={'No'}>No</MenuItem>
                                                <MenuItem value={'Yes'}>Yes</MenuItem>
                                              </Select>
                                            </FormControl>
                                          </Box>
                                        </Tooltip>
                                        {touched.fixedStopLoss && errors.fixedStopLoss && (
                                          <FormHelperText error id="helper-text-fixedStopLoss">
                                            {errors.fixedStopLoss}
                                          </FormHelperText>
                                        )}
                                      </Stack>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Stack spacing={1}>
                                        <Typography
                                          variant="subtitle2"
                                          fontWeight={500}
                                          mb={1}
                                          color="textSecondary"
                                          fontSize={16}
                                          lineHeight="100%"
                                        >
                                          Enter Fixed Stop Loss
                                        </Typography>
                                        <TextField
                                          variant="outlined"
                                          fullWidth
                                          id="fixedStopLossValue"
                                          name="fixedStopLossValue"
                                          type="number"
                                          placeholder="Enter Fixed Stop Loss"
                                          value={values?.fixedStopLoss === 'Yes' ? values.fixedStopLossValue : ''}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={Boolean(touched.fixedStopLossValue && errors.fixedStopLossValue)}
                                          helperText={touched.fixedStopLossValue && errors.fixedStopLossValue}
                                          disabled={(values.fixedStopLoss !== 'Yes' || values.copyStopLoss) && !values.strictClose}
                                          sx={inputStyle}
                                        />
                                      </Stack>
                                    </Grid>
                                  </Grid>
                                  <Grid item xs={12} sx={{ mt: 3 }} justifyContent={'space-between'} display={'flex'}>
                                    <Grid sx={{ mr: 2 }} item xs={6}>
                                      <Stack spacing={1}>
                                        <Tooltip
                                          title={
                                            !values?.copyTakeProfit ? (
                                              ''
                                            ) : (
                                              <Typography
                                                color={
                                                  theme.palette.mode === 'dark'
                                                    ? theme.palette.background.paper
                                                    : theme.typography.caption.color
                                                }
                                                variant={'caption'}
                                              >
                                                Remove copy take profit first
                                              </Typography>
                                            )
                                          }
                                        >
                                          <Box>
                                            <Typography
                                              variant="subtitle2"
                                              fontWeight={500}
                                              mb={1}
                                              color="textSecondary"
                                              fontSize={16}
                                              lineHeight="100%"
                                            >
                                              Fixed Take Profit (pips)
                                            </Typography>
                                            <FormControl fullWidth error={Boolean(touched.fixedTakeProfit && errors.fixedTakeProfit)}>
                                              <Select
                                                id="fixedTakeProfit"
                                                name="fixedTakeProfit"
                                                value={values.fixedTakeProfit}
                                                onBlur={handleBlur}
                                                MenuProps={{
                                                  PaperProps: {
                                                    sx: {
                                                      zIndex: 9999,
                                                      maxHeight: 300,
                                                      borderRadius: '6px'
                                                    }
                                                  },
                                                  sx: {
                                                    zIndex: 9999
                                                  }
                                                }}
                                                sx={selectStyle}
                                                onChange={(e, v) => {
                                                  setFieldValue('fixedTakeProfit', e?.target?.value);
                                                  setFieldValue('fixedTakeProfitValue', '');
                                                  if (e?.target?.value === 'Yes' && values.copyTakeProfit && values.strictClose) {
                                                    setFieldValue('copyTakeProfit', false);
                                                  } else if (e?.target?.value === 'No' && !values.copyTakeProfit && values.strictClose) {
                                                    setFieldValue('copyTakeProfit', true);
                                                  }
                                                }}
                                                disabled={values.copyTakeProfit && !values.strictClose}
                                                error={Boolean(touched.fixedTakeProfit && errors.fixedTakeProfit)}
                                              >
                                                <MenuItem value={'No'}>No</MenuItem>
                                                <MenuItem value={'Yes'}>Yes</MenuItem>
                                              </Select>
                                            </FormControl>
                                          </Box>
                                        </Tooltip>
                                        {touched.fixedTakeProfit && errors.fixedTakeProfit && (
                                          <FormHelperText error id="helper-text-fixedTakeProfit">
                                            {errors.fixedTakeProfit}
                                          </FormHelperText>
                                        )}
                                      </Stack>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Stack spacing={1}>
                                        <Typography
                                          variant="subtitle2"
                                          fontWeight={500}
                                          mb={1}
                                          color="textSecondary"
                                          fontSize={16}
                                          lineHeight="100%"
                                        >
                                          Enter Fixed Take Profit
                                        </Typography>
                                        <TextField
                                          variant="outlined"
                                          fullWidth
                                          id="fixedTakeProfitValue"
                                          name="fixedTakeProfitValue"
                                          type="number"
                                          placeholder="Enter Fixed Take Profit"
                                          value={values?.fixedTakeProfit === 'Yes' ? values.fixedTakeProfitValue : ''}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={Boolean(touched.fixedTakeProfitValue && errors.fixedTakeProfitValue)}
                                          helperText={touched.fixedTakeProfitValue && errors.fixedTakeProfitValue}
                                          disabled={(values.fixedTakeProfit !== 'Yes' || values.copyTakeProfit) && !values.strictClose}
                                          sx={inputStyle}
                                        />
                                      </Stack>
                                    </Grid>
                                  </Grid>
                                  <Grid item xs={12} sx={{ mt: 3 }} justifyContent={'space-between'} display={'flex'}>
                                    <Grid sx={{ mr: 2 }} item xs={6}>
                                      <Stack spacing={1}>
                                        <Tooltip
                                          title={
                                            values?.copyStopLoss ? (
                                              ''
                                            ) : (
                                              <Typography
                                                color={
                                                  theme.palette.mode === 'dark'
                                                    ? theme.palette.background.paper
                                                    : theme.typography.caption.color
                                                }
                                                variant={'caption'}
                                              >
                                                Select copy stop loss first
                                              </Typography>
                                            )
                                          }
                                        >
                                          <Box>
                                            <Typography
                                              variant="subtitle2"
                                              fontWeight={500}
                                              mb={1}
                                              color="textSecondary"
                                              fontSize={16}
                                              lineHeight="100%"
                                            >
                                              Stop Loss Refinement (pips)
                                            </Typography>
                                            <FormControl fullWidth error={Boolean(touched.stopLossRefinement && errors.stopLossRefinement)}>
                                              <Select
                                                id="stopLossRefinement"
                                                name="stopLossRefinement"
                                                value={values.stopLossRefinement}
                                                onBlur={handleBlur}
                                                MenuProps={{
                                                  PaperProps: {
                                                    sx: {
                                                      zIndex: 9999,
                                                      maxHeight: 300,
                                                      borderRadius: '6px'
                                                    }
                                                  },
                                                  sx: {
                                                    zIndex: 9999
                                                  }
                                                }}
                                                sx={selectStyle}
                                                onChange={(e, v) => {
                                                  setFieldValue('stopLossRefinement', e?.target?.value);
                                                  setFieldValue('stopLossRefinementValue', '');
                                                }}
                                                disabled={!values.copyStopLoss}
                                                error={Boolean(touched.stopLossRefinement && errors.stopLossRefinement)}
                                              >
                                                <MenuItem value={'No'}>No</MenuItem>
                                                <MenuItem value={'Yes'}>Yes</MenuItem>
                                              </Select>
                                            </FormControl>
                                          </Box>
                                        </Tooltip>
                                        {touched.stopLossRefinement && errors.stopLossRefinement && (
                                          <FormHelperText error id="helper-text-stopLossRefinement">
                                            {errors.stopLossRefinement}
                                          </FormHelperText>
                                        )}
                                      </Stack>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Stack spacing={1}>
                                        <Typography
                                          variant="subtitle2"
                                          fontWeight={500}
                                          mb={1}
                                          color="textSecondary"
                                          fontSize={16}
                                          lineHeight="100%"
                                        >
                                          Enter Refinement
                                        </Typography>
                                        <TextField
                                          variant="outlined"
                                          fullWidth
                                          id="stopLossRefinementValue"
                                          type="number"
                                          value={values?.stopLossRefinement === 'Yes' ? values.stopLossRefinementValue : ''}
                                          name="stopLossRefinementValue"
                                          onBlur={handleBlur}
                                          onChange={handleChange}
                                          placeholder="Enter Refinement"
                                          disabled={values.stopLossRefinement !== 'Yes' || !values.copyStopLoss}
                                          error={Boolean(touched.stopLossRefinementValue && errors.stopLossRefinementValue)}
                                          helperText={touched.stopLossRefinementValue && errors.stopLossRefinementValue}
                                          sx={inputStyle}
                                        />
                                      </Stack>
                                    </Grid>
                                  </Grid>
                                  <Grid item xs={12} sx={{ mt: 3 }} justifyContent={'space-between'} display={'flex'}>
                                    <Grid sx={{ mr: 2 }} item xs={6}>
                                      <Stack spacing={1}>
                                        <Tooltip
                                          title={
                                            values?.copyTakeProfit ? (
                                              ''
                                            ) : (
                                              <Typography
                                                color={
                                                  theme.palette.mode === 'dark'
                                                    ? theme.palette.background.paper
                                                    : theme.typography.caption.color
                                                }
                                                variant={'caption'}
                                              >
                                                Select copy take profit first
                                              </Typography>
                                            )
                                          }
                                        >
                                          <Box>
                                            <Typography
                                              variant="subtitle2"
                                              fontWeight={500}
                                              mb={1}
                                              color="textSecondary"
                                              fontSize={16}
                                              lineHeight="100%"
                                            >
                                              Take Profit Refinement (pips)
                                            </Typography>
                                            <FormControl
                                              fullWidth
                                              error={Boolean(touched.takeProfitRefinement && errors.takeProfitRefinement)}
                                            >
                                              <Select
                                                id="takeProfitRefinement"
                                                name="takeProfitRefinement"
                                                value={values.takeProfitRefinement}
                                                onBlur={handleBlur}
                                                MenuProps={{
                                                  PaperProps: {
                                                    sx: {
                                                      zIndex: 9999,
                                                      maxHeight: 300,
                                                      borderRadius: '6px'
                                                    }
                                                  },
                                                  sx: {
                                                    zIndex: 9999
                                                  }
                                                }}
                                                sx={selectStyle}
                                                onChange={(e, v) => {
                                                  setFieldValue('takeProfitRefinement', e?.target?.value);
                                                  setFieldValue('takeProfitRefinementValue', '');
                                                }}
                                                disabled={!values.copyTakeProfit}
                                                error={Boolean(touched.takeProfitRefinement && errors.takeProfitRefinement)}
                                              >
                                                <MenuItem value={'No'}>No</MenuItem>
                                                <MenuItem value={'Yes'}>Yes</MenuItem>
                                              </Select>
                                            </FormControl>
                                          </Box>
                                        </Tooltip>
                                        {touched.takeProfitRefinement && errors.takeProfitRefinement && (
                                          <FormHelperText error id="helper-text-takeProfitRefinement">
                                            {errors.takeProfitRefinement}
                                          </FormHelperText>
                                        )}
                                      </Stack>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Stack spacing={1}>
                                        <Typography
                                          variant="subtitle2"
                                          fontWeight={500}
                                          mb={1}
                                          color="textSecondary"
                                          fontSize={16}
                                          lineHeight="100%"
                                        >
                                          Enter Refinement
                                        </Typography>
                                        <TextField
                                          variant="outlined"
                                          fullWidth
                                          id="takeProfitRefinementValue"
                                          type="number"
                                          value={values?.takeProfitRefinement === 'Yes' ? values.takeProfitRefinementValue : ''}
                                          name="takeProfitRefinementValue"
                                          onBlur={handleBlur}
                                          onChange={handleChange}
                                          placeholder="Enter Refinement"
                                          disabled={values.takeProfitRefinement !== 'Yes' || !values.copyTakeProfit}
                                          error={Boolean(touched.takeProfitRefinementValue && errors.takeProfitRefinementValue)}
                                          helperText={touched.takeProfitRefinementValue && errors.takeProfitRefinementValue}
                                          sx={inputStyle}
                                        />
                                      </Stack>
                                    </Grid>
                                  </Grid>
                                </AccordionDetails>
                              </Accordion>
                            </Grid>

                            <Grid item xs={12} style={{ paddingTop: '12px' }}>
                              <Accordion expanded={advanceExpanded} sx={accordionStyle}>
                                <AccordionSummary
                                  expandIcon={
                                    <ChevronIcon
                                      style={{
                                        width: 16,
                                        height: 16,
                                        transform: advanceExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.2s'
                                      }}
                                    />
                                  }
                                  onClick={(e) => {
                                    if (!advanceExpanded) {
                                      setAdvanceExpanded(true);
                                      setSlTpExpand(false);
                                      setFilterExpanded(false);
                                      setCopyExpanded(false);
                                    }
                                  }}
                                  sx={accordionSummaryStyle}
                                >
                                  <SentimentsIcon style={{ width: 16, height: 16, marginRight: 8 }} />
                                  <Typography
                                    variant="body1"
                                    component="span"
                                    color="textPrimary"
                                    fontWeight="600"
                                    sx={{ fontSize: textSizeByBreakpoint(16) }}
                                  >
                                    Advanced Settings
                                  </Typography>
                                </AccordionSummary>

                                <AccordionDetails sx={accordionDetailsStyle}>
                                  <Grid container spacing={2}>
                                    {/* First Row of Checkboxes */}
                                    <Grid item xs={12}>
                                      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
                                        <FormControlLabel
                                          control={
                                            <Checkbox
                                              name="forceMinimumLotSize"
                                              checked={values.forceMinimumLotSize}
                                              onChange={handleChange}
                                              sx={checkboxStyle}
                                            />
                                          }
                                          label="Force Minimum Lot Size"
                                        />
                                        <FormControlLabel
                                          control={
                                            <Checkbox
                                              name="forceMaximumLotSize"
                                              checked={values.forceMaximumLotSize}
                                              onChange={handleChange}
                                              sx={checkboxStyle}
                                            />
                                          }
                                          label="Force Maximum Lot Size"
                                        />
                                        {isCTIAccount || isSeacrest ? (
                                          <Tooltip
                                            title={
                                              <Typography
                                                color={
                                                  theme.palette.mode === 'dark'
                                                    ? theme.palette.background.paper
                                                    : theme.typography.caption.color
                                                }
                                                variant={'caption'}
                                              >
                                                {isSeacrest ? 'Seacrest Markets' : 'CTI'} does not allow this feature
                                              </Typography>
                                            }
                                          >
                                            <FormControlLabel
                                              control={
                                                <Checkbox
                                                  name="reverseTrade"
                                                  checked={values.reverseTrade}
                                                  disabled={isCTIAccount || isSeacrest}
                                                  sx={checkboxStyle}
                                                />
                                              }
                                              label="Reverse Trade"
                                            />
                                          </Tooltip>
                                        ) : (
                                          <FormControlLabel
                                            control={
                                              <Checkbox
                                                name="reverseTrade"
                                                checked={values.reverseTrade}
                                                onChange={(e, value) => {
                                                  setFieldValue('reverseTrade', value);
                                                  if (value) {
                                                    setFieldValue('copyPendingOrders', false);
                                                  }
                                                }}
                                                sx={checkboxStyle}
                                              />
                                            }
                                            label="Reverse Trade"
                                          />
                                        )}
                                        <FormControlLabel
                                          control={
                                            <Checkbox
                                              name="copyExistingTrades"
                                              checked={values.copyExistingTrades}
                                              onChange={(event, checked) => {
                                                setFieldValue('copyExistingTrades', checked);
                                              }}
                                              sx={checkboxStyle}
                                            />
                                          }
                                          label="Copy Existing Trades"
                                        />
                                      </Box>
                                    </Grid>

                                    {/* Second Row of Checkboxes */}
                                    <Grid item xs={12}>
                                      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
                                        <FormControlLabel
                                          control={
                                            <Checkbox
                                              name="considerContractSize"
                                              checked={values.considerContractSize}
                                              disabled={+values.riskType === RISK_TYPE.BALANCE_PERCENTAGE}
                                              onChange={(e, value) => {
                                                setFieldValue('considerContractSize', value);
                                              }}
                                              sx={checkboxStyle}
                                            />
                                          }
                                          label="Contract Alignment"
                                        />
                                        <FormControlLabel
                                          control={
                                            <Checkbox
                                              name="copyExpiryTime"
                                              checked={values.copyExpiryTime}
                                              onChange={(e, value) => {
                                                setFieldValue('copyExpiryTime', value);
                                              }}
                                              sx={checkboxStyle}
                                            />
                                          }
                                          label="Copy Expiry Time"
                                        />
                                        <FormControlLabel
                                          control={
                                            <Checkbox
                                              name="strictClose"
                                              checked={values.strictClose}
                                              onChange={(e, value) => {
                                                setFieldValue('strictClose', value);
                                                if (value) {
                                                  if (values.fixedStopLoss === 'No') {
                                                    setFieldValue('copyStopLoss', true);
                                                  }
                                                  if (values.fixedTakeProfit === 'No') {
                                                    setFieldValue('copyTakeProfit', true);
                                                  }
                                                }
                                              }}
                                              sx={checkboxStyle}
                                            />
                                          }
                                          label={
                                            <Box display="flex" alignItems="center" gap={1}>
                                              Strict Close
                                              <Tooltip
                                                slotProps={{
                                                  popper: {
                                                    sx: {
                                                      zIndex: (theme) => theme.zIndex.drawer + 1100
                                                    }
                                                  }
                                                }}
                                                title={
                                                  <Typography
                                                    color={
                                                      theme.palette.mode === 'dark'
                                                        ? theme.palette.background.paper
                                                        : theme.typography.caption.color
                                                    }
                                                    variant={'caption'}
                                                  >
                                                    High risk option, click to learn more
                                                  </Typography>
                                                }
                                              >
                                                <WarningOutlined
                                                  style={{ color: theme.palette.error.main, verticalAlign: 'middle', cursor: 'pointer' }}
                                                  onClick={() => {
                                                    window.open(
                                                      'https://docs.tradersconnect.com/trade-copier/copier-options/#strict-close',
                                                      '_blank'
                                                    );
                                                  }}
                                                />
                                              </Tooltip>
                                            </Box>
                                          }
                                        />
                                      </Box>
                                    </Grid>

                                    {/* Copy Trade Comment Section */}
                                    {isComment(values?.copyTo) && (
                                      <>
                                        <Grid item xs={12} md={6}>
                                          <Typography
                                            variant="subtitle2"
                                            fontWeight={500}
                                            mb={1}
                                            color="textSecondary"
                                            fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                                            lineHeight="100%"
                                          >
                                            Copy Trade Comment
                                          </Typography>
                                          {!isSeacrest ? (
                                            <FormControl fullWidth>
                                              <Select
                                                value={values.copyComment}
                                                onBlur={handleBlur}
                                                onChange={(e) => {
                                                  setFieldValue('copyComment', e?.target?.value);
                                                  setFieldValue('customComment', '');
                                                }}
                                                error={Boolean(touched.copyComment && errors.copyComment)}
                                                MenuProps={{
                                                  PaperProps: {
                                                    sx: {
                                                      zIndex: 9999
                                                    }
                                                  },
                                                  sx: {
                                                    zIndex: 9999
                                                  }
                                                }}
                                                sx={selectStyle}
                                              >
                                                <MenuItem value={'No'}>No</MenuItem>
                                                <MenuItem value={'Yes'}>Yes</MenuItem>
                                                <MenuItem value={'Custom'}>Custom</MenuItem>
                                              </Select>
                                            </FormControl>
                                          ) : (
                                            <Tooltip
                                              title={
                                                <Typography
                                                  color={
                                                    theme.palette.mode === 'dark'
                                                      ? theme.palette.background.paper
                                                      : theme.typography.caption.color
                                                  }
                                                  variant={'caption'}
                                                >
                                                  Seacrest Markets does not allow this feature
                                                </Typography>
                                              }
                                            >
                                              <FormControl fullWidth>
                                                <Select
                                                  value={values.copyComment}
                                                  onBlur={handleBlur}
                                                  disabled={isSeacrest}
                                                  onChange={(e) => {
                                                    setFieldValue('copyComment', e?.target?.value);
                                                    setFieldValue('customComment', '');
                                                  }}
                                                  error={Boolean(touched.copyComment && errors.copyComment)}
                                                  MenuProps={{
                                                    PaperProps: {
                                                      sx: {
                                                        zIndex: 9999
                                                      }
                                                    },
                                                    sx: {
                                                      zIndex: 9999
                                                    }
                                                  }}
                                                  sx={selectStyle}
                                                >
                                                  <MenuItem value={'No'}>No</MenuItem>
                                                  <MenuItem value={'Yes'}>Yes</MenuItem>
                                                  <MenuItem value={'Custom'}>Custom</MenuItem>
                                                </Select>
                                              </FormControl>
                                            </Tooltip>
                                          )}
                                          {touched.copyComment && errors.copyComment && (
                                            <FormHelperText error id="helper-text-copyComment">
                                              {errors.copyComment}
                                            </FormHelperText>
                                          )}
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                          <Typography
                                            variant="subtitle2"
                                            fontWeight={500}
                                            mb={1}
                                            color="textSecondary"
                                            fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                                            lineHeight="100%"
                                          >
                                            Custom Comment
                                          </Typography>
                                          <TextField
                                            variant="outlined"
                                            fullWidth
                                            name="customComment"
                                            placeholder="Custom Comment"
                                            value={values?.copyComment === 'Custom' ? values.customComment : ''}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={Boolean(touched.customComment && errors.customComment)}
                                            helperText={touched.customComment && errors.customComment ? 'Maximum length of custom comment is 25' : ''}
                                            disabled={values.copyComment !== 'Custom'}
                                            sx={inputStyle}
                                          />
                                        </Grid>
                                      </>
                                    )}

                                    {/* Copy Magic Number Section */}
                                    <Grid item xs={12} md={6}>
                                      <Typography
                                        variant="subtitle2"
                                        fontWeight={500}
                                        mb={1}
                                        color="textSecondary"
                                        fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                                        lineHeight="100%"
                                      >
                                        Copy Magic Number
                                      </Typography>
                                      {isCTIAccount || isSeacrest || hasTVAccount() ? (
                                        <Tooltip
                                          title={
                                            <Typography
                                              color={
                                                theme.palette.mode === 'dark'
                                                  ? theme.palette.background.paper
                                                  : theme.typography.caption.color
                                              }
                                              variant={'caption'}
                                            >
                                              {hasTVAccount() ? 'Tradovate' : isSeacrest ? 'Seacrest Markets' : 'CTI'} does not allow this feature
                                            </Typography>
                                          }
                                        >
                                          <FormControl fullWidth>
                                            <Select
                                              value={values.copyMagicNumber}
                                              disabled={isCTIAccount || isSeacrest || hasTVAccount()}
                                              onBlur={handleBlur}
                                              onChange={(e) => {
                                                setFieldValue('copyMagicNumber', e?.target?.value);
                                                setFieldValue('magicNumber', '');
                                              }}
                                              error={Boolean(touched.copyMagicNumber && errors.copyMagicNumber)}
                                              MenuProps={{
                                                PaperProps: {
                                                  sx: {
                                                    zIndex: 9999
                                                  }
                                                },
                                                sx: {
                                                  zIndex: 9999
                                                }
                                              }}
                                              sx={selectStyle}
                                            >
                                              <MenuItem value={'No'}>No</MenuItem>
                                              <MenuItem value={'Yes'}>Yes</MenuItem>
                                              <MenuItem value={'Custom'}>Custom</MenuItem>
                                            </Select>
                                          </FormControl>
                                        </Tooltip>
                                      ) : (
                                        <FormControl fullWidth>
                                          <Select
                                            value={values.copyMagicNumber}
                                            onBlur={handleBlur}
                                            onChange={(e) => {
                                              setFieldValue('copyMagicNumber', e?.target?.value);
                                              setFieldValue('magicNumber', '');
                                            }}
                                            error={Boolean(touched.copyMagicNumber && errors.copyMagicNumber)}
                                            MenuProps={{
                                              PaperProps: {
                                                sx: {
                                                  zIndex: 9999
                                                }
                                              },
                                              sx: {
                                                zIndex: 9999
                                              }
                                            }}
                                            sx={selectStyle}
                                          >
                                            <MenuItem value={'No'}>No</MenuItem>
                                            <MenuItem value={'Yes'}>Yes</MenuItem>
                                            <MenuItem value={'Custom'}>Custom</MenuItem>
                                          </Select>
                                        </FormControl>
                                      )}
                                      {touched.copyMagicNumber && errors.copyMagicNumber && (
                                        <FormHelperText error id="helper-text-copyMagicNumber">
                                          {errors.copyMagicNumber}
                                        </FormHelperText>
                                      )}
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                      <Typography
                                        variant="subtitle2"
                                        fontWeight={500}
                                        mb={1}
                                        color="textSecondary"
                                        fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                                        lineHeight="100%"
                                      >
                                        Enter Magic Number
                                      </Typography>
                                      <TextField
                                        variant="outlined"
                                        fullWidth
                                        name="magicNumber"
                                        placeholder="Enter Magic Number"
                                        value={values?.copyMagicNumber === 'Custom' ? values.magicNumber : ''}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={Boolean(touched.magicNumber && errors.magicNumber)}
                                        helperText={touched.magicNumber && errors.magicNumber}
                                        disabled={values.copyMagicNumber !== 'Custom'}
                                        sx={inputStyle}
                                      />
                                    </Grid>

                                    {/* Delay Type Section */}
                                    <Grid item xs={12} md={6}>
                                      <Typography
                                        variant="subtitle2"
                                        fontWeight={500}
                                        mb={1}
                                        color="textSecondary"
                                        fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                                        lineHeight="100%"
                                      >
                                        Delay type
                                      </Typography>
                                      {isCTIAccount || isSeacrest ? (
                                        <Tooltip
                                          title={
                                            <Typography
                                              color={
                                                theme.palette.mode === 'dark'
                                                  ? theme.palette.background.paper
                                                  : theme.typography.caption.color
                                              }
                                              variant={'caption'}
                                            >
                                              {isSeacrest ? 'Seacrest Markets' : 'CTI'} does not allow this feature
                                            </Typography>
                                          }
                                        >
                                          <FormControl fullWidth>
                                            <Select
                                              value={values.delayType}
                                              disabled={isCTIAccount || isSeacrest}
                                              onBlur={handleBlur}
                                              onChange={(e) => {
                                                setFieldValue('delayType', e?.target?.value);
                                                setFieldValue('tradeDelayMin', '');
                                                setFieldValue('tradeDelayMax', '');
                                              }}
                                              error={Boolean(touched.delayType && errors.delayType)}
                                              MenuProps={{
                                                PaperProps: {
                                                  sx: {
                                                    zIndex: 9999
                                                  }
                                                },
                                                sx: {
                                                  zIndex: 9999
                                                }
                                              }}
                                              sx={selectStyle}
                                            >
                                              <MenuItem value={'no'}>No trade delay</MenuItem>
                                              <MenuItem value={'fixed'}>Fixed trade delay</MenuItem>
                                              <MenuItem value={'random'}>Random trade delay</MenuItem>
                                            </Select>
                                          </FormControl>
                                        </Tooltip>
                                      ) : (
                                        <FormControl fullWidth>
                                          <Select
                                            value={values.delayType}
                                            onBlur={handleBlur}
                                            onChange={(e) => {
                                              setFieldValue('delayType', e?.target?.value);
                                              setFieldValue('tradeDelayMin', '');
                                              setFieldValue('tradeDelayMax', '');
                                            }}
                                            error={Boolean(touched.delayType && errors.delayType)}
                                            MenuProps={{
                                              PaperProps: {
                                                sx: {
                                                  zIndex: 9999
                                                }
                                              },
                                              sx: {
                                                zIndex: 9999
                                              }
                                            }}
                                            sx={selectStyle}
                                          >
                                            <MenuItem value={'no'}>No trade delay</MenuItem>
                                            <MenuItem value={'fixed'}>Fixed trade delay</MenuItem>
                                            <MenuItem value={'random'}>Random trade delay</MenuItem>
                                          </Select>
                                        </FormControl>
                                      )}
                                      {touched.delayType && errors.delayType && (
                                        <FormHelperText error id="helper-text-delayType">
                                          {errors.delayType}
                                        </FormHelperText>
                                      )}
                                    </Grid>

                                    {/* Trade Delay Min/Max */}
                                    <Grid item xs={12} md={6}>
                                      <Typography
                                        variant="subtitle2"
                                        fontWeight={500}
                                        mb={1}
                                        color="textSecondary"
                                        fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                                        lineHeight="100%"
                                      >
                                        Trade Delay (ms){values.delayType === 'random' ? ' - Minimum' : ''}
                                      </Typography>
                                      <TextField
                                        variant="outlined"
                                        fullWidth
                                        name="tradeDelayMin"
                                        placeholder={`Trade Delay (ms)${values.delayType === 'random' ? ' - Minimum' : ''}`}
                                        value={values.tradeDelayMin}
                                        onChange={(e) => {
                                          const numericInput = e.target.value.replace(/\D/g, '');
                                          handleChange({
                                            target: {
                                              name: 'tradeDelayMin',
                                              value: numericInput
                                            }
                                          });
                                        }}
                                        onBlur={handleBlur}
                                        error={Boolean(touched.tradeDelayMin && errors.tradeDelayMin)}
                                        helperText={touched.tradeDelayMin && errors.tradeDelayMin}
                                        disabled={values.delayType === 'no'}
                                        sx={inputStyle}
                                      />
                                    </Grid>

                                    {values.delayType === 'random' && (
                                      <Grid item xs={12} md={6}>
                                        <Typography
                                          variant="subtitle2"
                                          fontWeight={500}
                                          mb={1}
                                          color="textSecondary"
                                          fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                                          lineHeight="100%"
                                        >
                                          Trade Delay (ms) - Maximum
                                        </Typography>
                                        <TextField
                                          variant="outlined"
                                          fullWidth
                                          name="tradeDelayMax"
                                          placeholder="Trade Delay (ms) - Maximum"
                                          value={values.tradeDelayMax}
                                          onChange={(e) => {
                                            const numericInput = e.target.value.replace(/\D/g, '');
                                            handleChange({
                                              target: {
                                                name: 'tradeDelayMax',
                                                value: numericInput
                                              }
                                            });
                                          }}
                                          onBlur={handleBlur}
                                          error={Boolean(touched.tradeDelayMax && errors.tradeDelayMax)}
                                          helperText={touched.tradeDelayMax && errors.tradeDelayMax}
                                          sx={inputStyle}
                                        />
                                      </Grid>
                                    )}

                                    {/* Lot Refiner */}
                                    <Grid item xs={12} md={6}>
                                      <Typography
                                        variant="subtitle2"
                                        fontWeight={500}
                                        mb={1}
                                        color="textSecondary"
                                        fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                                        lineHeight="100%"
                                      >
                                        Lot Refiner
                                      </Typography>
                                      <TextField
                                        variant="outlined"
                                        fullWidth
                                        name="lotRefiner"
                                        placeholder="Lot Refiner"
                                        value={values.lotRefiner}
                                        onChange={(e) => {
                                          if (hasTVAccount()) {
                                            const value = e.target.value;
                                            if (value.includes('.')) {
                                              e.target.value = Math.round(parseFloat(value)).toString();
                                            }
                                          }
                                          handleChange(e);
                                        }}
                                        onBlur={handleBlur}
                                        error={Boolean(touched.lotRefiner && errors.lotRefiner)}
                                        helperText={touched.lotRefiner && errors.lotRefiner}
                                        sx={inputStyle}
                                        InputProps={{
                                          inputProps: {
                                            step: hasTVAccount() ? '1' : '0.01'
                                          }
                                        }}
                                      />
                                    </Grid>
                                  </Grid>
                                </AccordionDetails>
                              </Accordion>
                            </Grid>

                            <Grid item xs={12} style={{ paddingTop: '12px' }}>
                              <Accordion expanded={filterExpanded} sx={accordionStyle}>
                                <AccordionSummary
                                  expandIcon={
                                    <ChevronIcon
                                      style={{
                                        width: 16,
                                        height: 16,
                                        transform: filterExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.2s'
                                      }}
                                    />
                                  }
                                  onClick={(e) => {
                                    if (!filterExpanded) {
                                      setFilterExpanded(true);
                                      setSlTpExpand(false);
                                      setAdvanceExpanded(false);
                                      setCopyExpanded(false);
                                    }
                                  }}
                                  sx={accordionSummaryStyle}
                                >
                                  <SentimentsIcon style={{ width: 16, height: 16, marginRight: 8 }} />
                                  <Typography
                                    variant="body1"
                                    component="span"
                                    color="textPrimary"
                                    fontWeight="600"
                                    sx={{ fontSize: textSizeByBreakpoint(16) }}
                                  >
                                    Trade Filters
                                  </Typography>
                                </AccordionSummary>

                                <AccordionDetails sx={accordionDetailsStyle}>
                                  <Grid container spacing={2}>
                                    {/* Filter by Magic Number */}
                                    <Grid item xs={12} md={6}>
                                      <Typography
                                        variant="subtitle2"
                                        fontWeight={500}
                                        mb={1}
                                        color="textSecondary"
                                        fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                                        lineHeight="100%"
                                      >
                                        Filter by magic number
                                      </Typography>
                                      {hasTVAccount() ? (
                                        <Tooltip
                                          title={
                                            <Typography
                                              color={
                                                theme.palette.mode === 'dark'
                                                  ? theme.palette.background.paper
                                                  : theme.typography.caption.color
                                              }
                                              variant={'caption'}
                                            >
                                              Tradovate master does not allow this feature
                                            </Typography>
                                          }
                                        >
                                          <FormControl fullWidth>
                                            <Select
                                              value={values.magicFilter}
                                              disabled={true}
                                              onBlur={handleBlur}
                                              error={Boolean(touched.magicFilter && errors.magicFilter)}
                                              MenuProps={{
                                                PaperProps: {
                                                  sx: {
                                                    zIndex: 9999
                                                  }
                                                },
                                                sx: {
                                                  zIndex: 9999
                                                }
                                              }}
                                              sx={selectStyle}
                                            >
                                              <MenuItem disabled value={'UNKNOWN'}>
                                                Select an option
                                              </MenuItem>
                                              <MenuItem value={'COPY'}>Copy</MenuItem>
                                              <MenuItem value={'IGNORE'}>Ignore</MenuItem>
                                            </Select>
                                          </FormControl>
                                        </Tooltip>
                                      ) : (
                                        <FormControl fullWidth>
                                          <Select
                                            value={values.magicFilter}
                                            onBlur={handleBlur}
                                            onChange={(e) => {
                                              setFieldValue('magicFilter', e?.target?.value);
                                              setFieldValue('magicFilterValue', '');
                                            }}
                                            error={Boolean(touched.magicFilter && errors.magicFilter)}
                                            MenuProps={{
                                              PaperProps: {
                                                sx: {
                                                  zIndex: 9999
                                                }
                                              },
                                              sx: {
                                                zIndex: 9999
                                              }
                                            }}
                                            sx={selectStyle}
                                          >
                                            <MenuItem disabled value={'UNKNOWN'}>
                                              Select an option
                                            </MenuItem>
                                            <MenuItem value={'COPY'}>Copy</MenuItem>
                                            <MenuItem value={'IGNORE'}>Ignore</MenuItem>
                                          </Select>
                                        </FormControl>
                                      )}
                                      {touched.magicFilter && errors.magicFilter && (
                                        <FormHelperText error id="helper-text-magicFilter">
                                          {errors.magicFilter}
                                        </FormHelperText>
                                      )}
                                    </Grid>

                                    {/* Magic Numbers */}
                                    <Grid item xs={12} md={6}>
                                      <Typography
                                        variant="subtitle2"
                                        fontWeight={500}
                                        mb={1}
                                        color="textSecondary"
                                        fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                                        lineHeight="100%"
                                      >
                                        Magic Numbers
                                      </Typography>
                                      <Autocomplete
                                        multiple
                                        id="magicFilterValue"
                                        open={false}
                                        forcePopupIcon={false}
                                        clearText={'Remove all'}
                                        options={selectedMagicNumbers}
                                        value={selectedMagicNumbers}
                                        getOptionLabel={(option: any) => option}
                                        disabled={values.magicFilter !== 'COPY' && values.magicFilter !== 'IGNORE'}
                                        readOnly={values.magicFilter !== 'COPY' && values.magicFilter !== 'IGNORE'}
                                        defaultValue={selectedMagicNumbers}
                                        onChange={(event, value) => {
                                          console.log(event, value);
                                          setSelectedMagicNumbers(value);
                                        }}
                                        renderInput={(params) => {
                                          return (
                                            <TextField
                                              {...params}
                                              placeholder="Press enter to add"
                                              value={currentMagicNumber}
                                              type="number"
                                              onChange={(e) => {
                                                setCurrentMagicNumber(e.target.value);
                                              }}
                                              onBlur={() => {
                                                console.log('currentMagicNumber: ', currentMagicNumber);
                                                if (currentMagicNumber) {
                                                  setSelectedMagicNumbers((prev) => [...prev, currentMagicNumber]);
                                                  setCurrentMagicNumber('');
                                                }
                                              }}
                                              onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                  e.preventDefault();
                                                  if (
                                                    currentMagicNumber &&
                                                    !selectedMagicNumbers.includes(currentMagicNumber) &&
                                                    selectedMagicNumbers?.length < 10
                                                  ) {
                                                    setSelectedMagicNumbers((prev) => [...prev, currentMagicNumber]);
                                                    setCurrentMagicNumber('');
                                                  } else if (selectedMagicNumbers.includes(currentMagicNumber)) {
                                                    service.dispatchSnackbar(
                                                      dispatch,
                                                      "Can't add duplicate magic filters",
                                                      'alert',
                                                      'error',
                                                      2000
                                                    );
                                                  } else if (selectedMagicNumbers?.length >= 10) {
                                                    service.dispatchSnackbar(
                                                      dispatch,
                                                      "Can't add more than 10 magic filters",
                                                      'alert',
                                                      'error',
                                                      2000
                                                    );
                                                  }
                                                }
                                                console.log('magicFilterValue: ', e);
                                              }}
                                              sx={inputStyle}
                                            />
                                          );
                                        }}
                                        sx={{
                                          '& .MuiAutocomplete-tag': {
                                            bgcolor: 'primary.lighter',
                                            border: '1px solid',
                                            borderRadius: 1,
                                            height: 32,
                                            pl: 1.5,
                                            pr: 1.5,
                                            lineHeight: '32px',
                                            borderColor: 'primary.light',
                                            '& .MuiChip-label': {
                                              paddingLeft: 0,
                                              paddingRight: 0
                                            },
                                            '& .MuiSvgIcon-root': {
                                              color: 'primary.main',
                                              ml: 1,
                                              mr: -0.75,
                                              '&:hover': {
                                                color: 'primary.dark'
                                              }
                                            }
                                          }
                                        }}
                                      />
                                      {touched.magicFilterValue && errors.magicFilterValue && (
                                        <FormHelperText error id="helper-text-magicFilterValue">
                                          {errors.magicFilterValue}
                                        </FormHelperText>
                                      )}
                                    </Grid>

                                    {/* Filter by Comment */}
                                    <Grid item xs={12} md={6}>
                                      <Typography
                                        variant="subtitle2"
                                        fontWeight={500}
                                        mb={1}
                                        color="textSecondary"
                                        fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                                        lineHeight="100%"
                                      >
                                        Filter by comment
                                      </Typography>
                                      {hasTVAccount() ? (
                                        <Tooltip
                                          title={
                                            <Typography
                                              color={
                                                theme.palette.mode === 'dark'
                                                  ? theme.palette.background.paper
                                                  : theme.typography.caption.color
                                              }
                                              variant={'caption'}
                                            >
                                              Tradovate master does not allow this feature
                                            </Typography>
                                          }
                                        >
                                          <FormControl fullWidth>
                                            <Select
                                              value={values.filterByComment}
                                              disabled={true}
                                              onBlur={handleBlur}
                                              error={Boolean(touched.filterByComment && errors.filterByComment)}
                                              MenuProps={{
                                                PaperProps: {
                                                  sx: {
                                                    zIndex: 9999
                                                  }
                                                },
                                                sx: {
                                                  zIndex: 9999
                                                }
                                              }}
                                              sx={selectStyle}
                                            >
                                              <MenuItem disabled value={'UNKNOWN'}>
                                                Select an option
                                              </MenuItem>
                                              <MenuItem value={'COPY'}>Copy</MenuItem>
                                              <MenuItem value={'IGNORE'}>Ignore</MenuItem>
                                            </Select>
                                          </FormControl>
                                        </Tooltip>
                                      ) : (
                                        <FormControl fullWidth>
                                          <Select
                                            value={values.filterByComment}
                                            onBlur={handleBlur}
                                            onChange={(e) => {
                                              setFieldValue('filterByComment', e?.target?.value);
                                              setFieldValue('filterByCommentValue', '');
                                            }}
                                            error={Boolean(touched.filterByComment && errors.filterByComment)}
                                            MenuProps={{
                                              PaperProps: {
                                                sx: {
                                                  zIndex: 9999
                                                }
                                              },
                                              sx: {
                                                zIndex: 9999
                                              }
                                            }}
                                            sx={selectStyle}
                                          >
                                            <MenuItem disabled value={'UNKNOWN'}>
                                              Select an option
                                            </MenuItem>
                                            <MenuItem value={'COPY'}>Copy</MenuItem>
                                            <MenuItem value={'IGNORE'}>Ignore</MenuItem>
                                          </Select>
                                        </FormControl>
                                      )}
                                      {touched.filterByComment && errors.filterByComment && (
                                        <FormHelperText error id="helper-text-filterByComment">
                                          {errors.filterByComment}
                                        </FormHelperText>
                                      )}
                                    </Grid>

                                    {/* Comments */}
                                    <Grid item xs={12} md={6}>
                                      <Typography
                                        variant="subtitle2"
                                        fontWeight={500}
                                        mb={1}
                                        color="textSecondary"
                                        fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                                        lineHeight="100%"
                                      >
                                        Comments
                                      </Typography>
                                      <Autocomplete
                                        multiple
                                        id="filterByCommentValue"
                                        open={false}
                                        forcePopupIcon={false}
                                        clearText={'Remove all'}
                                        options={selectedComments}
                                        value={selectedComments}
                                        getOptionLabel={(option: any) => option}
                                        disabled={values.filterByComment !== 'COPY' && values.filterByComment !== 'IGNORE'}
                                        readOnly={values.filterByComment !== 'COPY' && values.filterByComment !== 'IGNORE'}
                                        defaultValue={selectedComments}
                                        onChange={(event, value) => {
                                          console.log(event, value);
                                          setSelectedComments(value);
                                        }}
                                        renderInput={(params) => {
                                          return (
                                            <TextField
                                              {...params}
                                              placeholder="Press enter to add"
                                              value={currentComment}
                                              onChange={(e) => {
                                                setCurrentComment(e.target.value);
                                              }}
                                              onBlur={() => {
                                                if (currentComment) {
                                                  setSelectedComments((prev) => [...prev, currentComment]);
                                                  setCurrentComment('');
                                                }
                                              }}
                                              onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                  e.preventDefault();
                                                  if (
                                                    currentComment &&
                                                    !selectedComments.includes(currentComment) &&
                                                    selectedComments.length < 10
                                                  ) {
                                                    setSelectedComments((prev) => [...prev, currentComment]);
                                                    setCurrentComment('');
                                                  } else if (selectedComments.includes(currentComment)) {
                                                    service.dispatchSnackbar(
                                                      dispatch,
                                                      "Can't add duplicate comment filters",
                                                      'alert',
                                                      'error',
                                                      2000
                                                    );
                                                  } else if (selectedComments.length >= 10) {
                                                    service.dispatchSnackbar(
                                                      dispatch,
                                                      "Can't add more than 10 comment filters",
                                                      'alert',
                                                      'error',
                                                      2000
                                                    );
                                                  }
                                                }
                                                console.log('filterByCommentValue: ', e);
                                              }}
                                              sx={inputStyle}
                                            />
                                          );
                                        }}
                                        sx={{
                                          '& .MuiAutocomplete-tag': {
                                            bgcolor: 'primary.lighter',
                                            border: '1px solid',
                                            borderRadius: 1,
                                            height: 32,
                                            pl: 1.5,
                                            pr: 1.5,
                                            lineHeight: '32px',
                                            borderColor: 'primary.light',
                                            '& .MuiChip-label': {
                                              paddingLeft: 0,
                                              paddingRight: 0,
                                              whiteSpace: 'pre'
                                            },
                                            '& .MuiSvgIcon-root': {
                                              color: 'primary.main',
                                              ml: 1,
                                              mr: -0.75,
                                              '&:hover': {
                                                color: 'primary.dark'
                                              }
                                            }
                                          }
                                        }}
                                      />
                                      {touched.filterByCommentValue && errors.filterByCommentValue && (
                                        <FormHelperText error id="helper-text-filterByCommentValue">
                                          {errors.filterByCommentValue}
                                        </FormHelperText>
                                      )}
                                    </Grid>

                                    {/* Trade Side */}
                                    <Grid item xs={12} md={6}>
                                      <Typography
                                        variant="subtitle2"
                                        fontWeight={500}
                                        mb={1}
                                        color="textSecondary"
                                        fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                                        lineHeight="100%"
                                      >
                                        Trade Side
                                      </Typography>
                                      <FormControl fullWidth>
                                        <Select
                                          value={values.directionType}
                                          onBlur={handleBlur}
                                          onChange={(e) => {
                                            setFieldValue('directionType', e.target.value);
                                          }}
                                          error={Boolean(touched.directionType && errors.directionType)}
                                          MenuProps={{
                                            PaperProps: {
                                              sx: {
                                                zIndex: 9999
                                              }
                                            },
                                            sx: {
                                              zIndex: 9999
                                            }
                                          }}
                                          sx={selectStyle}
                                        >
                                          <MenuItem value={'ALL'}>Copy All trades</MenuItem>
                                          <MenuItem value={'BUY'}>Copy Buy trades</MenuItem>
                                          <MenuItem value={'SELL'}>Copy Sell trades</MenuItem>
                                        </Select>
                                      </FormControl>
                                      {touched.directionType && errors.directionType && (
                                        <FormHelperText error id="helper-text-directionType">
                                          {errors.directionType}
                                        </FormHelperText>
                                      )}
                                    </Grid>

                                    {/* Filter by Lot Size */}
                                    <Grid item xs={12} md={6}>
                                      <Typography
                                        variant="subtitle2"
                                        fontWeight={500}
                                        mb={1}
                                        color="textSecondary"
                                        fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                                        lineHeight="100%"
                                      >
                                        Filter by lot size
                                      </Typography>
                                      <FormControl fullWidth>
                                        <Select
                                          value={values.filterByLotSize}
                                          onBlur={handleBlur}
                                          onChange={(e) => {
                                            setFieldValue('filterByLotSize', e?.target?.value);
                                            setFieldValue('filterByLotSizeMinValue', '');
                                            setFieldValue('filterByLotSizeMaxValue', '');
                                          }}
                                          error={Boolean(touched.filterByLotSize && errors.filterByLotSize)}
                                          MenuProps={{
                                            PaperProps: {
                                              sx: {
                                                zIndex: 9999
                                              }
                                            },
                                            sx: {
                                              zIndex: 9999
                                            }
                                          }}
                                          sx={selectStyle}
                                        >
                                          <MenuItem value={'No'}>No</MenuItem>
                                          <MenuItem value={'Yes'}>Yes</MenuItem>
                                        </Select>
                                      </FormControl>
                                      {touched.filterByLotSize && errors.filterByLotSize && (
                                        <FormHelperText error id="helper-text-filterByLotSize">
                                          {errors.filterByLotSize}
                                        </FormHelperText>
                                      )}
                                    </Grid>

                                    {/* Enter minimum lot size */}
                                    <Grid item xs={12} md={6}>
                                      <Typography
                                        variant="subtitle2"
                                        fontWeight={500}
                                        mb={1}
                                        color="textSecondary"
                                        fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                                        lineHeight="100%"
                                      >
                                        Enter minimum lot size
                                      </Typography>
                                      <TextField
                                        variant="outlined"
                                        fullWidth
                                        name="filterByLotSizeMinValue"
                                        placeholder="Enter minimum lot size"
                                        value={values?.filterByLotSize === 'Yes' ? values.filterByLotSizeMinValue : ''}
                                        onChange={(e) => {
                                          if (hasTVAccount()) {
                                            const value = e.target.value;
                                            if (value.includes('.')) {
                                              e.target.value = Math.round(parseFloat(value)).toString();
                                            }
                                          }
                                          handleChange(e);
                                        }}
                                        onBlur={handleBlur}
                                        error={Boolean(touched.filterByLotSizeMinValue && errors.filterByLotSizeMinValue)}
                                        helperText={touched.filterByLotSizeMinValue && errors.filterByLotSizeMinValue}
                                        disabled={values.filterByLotSize !== 'Yes'}
                                        sx={inputStyle}
                                        InputProps={{
                                          inputProps: {
                                            step: hasTVAccount() ? '1' : '0.01'
                                          }
                                        }}
                                      />
                                    </Grid>

                                    {/* Enter maximum lot size */}
                                    <Grid item xs={12} md={6}>
                                      <Typography
                                        variant="subtitle2"
                                        fontWeight={500}
                                        mb={1}
                                        color="textSecondary"
                                        fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                                        lineHeight="100%"
                                      >
                                        Enter maximum lot size
                                      </Typography>
                                      <TextField
                                        variant="outlined"
                                        fullWidth
                                        name="filterByLotSizeMaxValue"
                                        placeholder="Enter maximum lot size"
                                        value={values?.filterByLotSize === 'Yes' ? values.filterByLotSizeMaxValue : ''}
                                        onChange={(e) => {
                                          if (hasTVAccount()) {
                                            const value = e.target.value;
                                            if (value.includes('.')) {
                                              e.target.value = Math.round(parseFloat(value)).toString();
                                            }
                                          }
                                          handleChange(e);
                                        }}
                                        onBlur={handleBlur}
                                        error={Boolean(touched.filterByLotSizeMaxValue && errors.filterByLotSizeMaxValue)}
                                        helperText={touched.filterByLotSizeMaxValue && errors.filterByLotSizeMaxValue}
                                        disabled={values.filterByLotSize !== 'Yes'}
                                        sx={inputStyle}
                                        InputProps={{
                                          inputProps: {
                                            step: hasTVAccount() ? '1' : '0.01'
                                          }
                                        }}
                                      />
                                    </Grid>
                                  </Grid>
                                </AccordionDetails>
                              </Accordion>
                            </Grid>

                            {errors.submit && (
                              <Grid item xs={12}>
                                <FormHelperText error>{errors.submit}</FormHelperText>
                              </Grid>
                            )}
                          </Grid>

                          <Grid
                            item
                            xs={12}
                            container
                            display={'-webkit-box'}
                            flexDirection={'column'}
                            justifyContent={'space-between'}
                            alignItems="flex-start"
                          >
                            <Grid item display="flex" justifyContent="start" alignItems="flex-start">
                              <img
                                onClick={() => {
                                  setOpenRiskCalculator(true);
                                }}
                                style={{ height: '22px', marginTop: 'auto', marginBottom: 'auto', marginRight: '8px', cursor: 'pointer' }}
                                src={CalculatorSvg}
                              />
                              <span style={{ lineHeight: '36.5px' }}>
                                Calculate your risk setting,
                                <span
                                  onClick={() => {
                                    setOpenRiskCalculator(true);
                                  }}
                                  style={{ cursor: 'pointer', color: theme.palette.primary.main }}
                                >
                                  {' '}
                                  click here
                                </span>
                              </span>
                            </Grid>
                            <Grid item display="flex" justifyContent="end" alignItems="flex-start">
                              <div style={{ marginRight: '8px' }}>
                                <Button
                                  sx={{ paddingX: '24px' }}
                                  onClick={() => {
                                    handleClose(hasUnsavedChanges(initialValues, values));
                                  }}
                                  disableElevation
                                  fullWidth
                                  variant="outlined"
                                  color="secondary"
                                >
                                  Cancel
                                </Button>
                              </div>
                              <div>
                                <LoadingButton
                                  sx={{ paddingX: '24px' }}
                                  loadingPosition="center"
                                  endIcon={<></>}
                                  loading={isSubmitting || apiCalling}
                                  disableElevation
                                  disabled={isSubmitting || apiCalling}
                                  fullWidth
                                  type="submit"
                                  variant="contained"
                                  color="primary"
                                  onClick={() => handleAddSlave(values)}
                                >
                                  Confirm
                                </LoadingButton>
                              </div>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Modal
                          open={openRiskCalculator}
                          onClose={() => {
                            setOpenRiskCalculator(false);
                          }}
                          aria-labelledby="child-modal-title"
                          aria-describedby="child-modal-description"
                          sx={{ zIndex: (theme) => theme.zIndex.drawer + 1100 }}
                        >
                          <Box sx={{ ...style, minWidth: 540, maxWidth: 620 }}>
                            <RiskCalculatorModal
                              handleClose={() => setOpenRiskCalculator(false)}
                              handleSuccess={(riskType: 'percentage' | 'lot' | 'fixedLot' | '' | null, value: string) => {
                                console.log(riskType, riskType === 'percentage' ? RISK_TYPE.BALANCE_MULTIPLIER : RISK_TYPE.LOT_MULTIPLIER);
                                if (riskType) {
                                  setFieldValue(
                                    'riskType',
                                    riskType === 'percentage'
                                      ? RISK_TYPE.BALANCE_MULTIPLIER
                                      : riskType === 'lot'
                                      ? RISK_TYPE.LOT_MULTIPLIER
                                      : RISK_TYPE.FIXED_LOT
                                  );
                                  if (riskType !== 'fixedLot') {
                                    setFieldValue('riskSettings', value);
                                  } else {
                                    setFieldValue('riskSettingsFixedLot', value);
                                  }
                                  if (riskType === 'percentage') {
                                    setFieldValue('considerContractSize', true);
                                  }
                                }
                                setOpenRiskCalculator(false);
                              }}
                            />
                          </Box>
                        </Modal>
                      </form>
                    )}
                  </Formik>
                </Grid>
              </StepWrapper>

              <StepWrapper value={activeStep} index={1}>
                {loadingCompare && (
                  <>
                    <Grid sx={{ minWidth: 720, minHeight: 220 }} display={'flex'} justifyContent={'center'}>
                      <Grid sx={{ height: '220px' }} display={'grid'} justifyContent={'center'}>
                        <Grid display={'grid'} justifyContent={'center'} sx={{ mb: 2, paddingTop: '60px' }}>
                          <CircularProgress sx={{ lineHeight: '220px' }} size={64} color={'primary'} />
                        </Grid>
                        <Grid sx={{ paddingBottom: '24px' }}>
                          <Typography variant={'h3'}>Verifying</Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </>
                )}
                {selectedSlaveId && selectedMasterId && selectedSlaveAccountId && symbolSuggestions?.length > 0 && !loadingCompare && (
                  <SymbolMismatchSelectionModal
                    slaveId={selectedSlaveId}
                    slaveAccount={selectedSlaveAccountId}
                    masterAccount={selectedMasterId}
                    suggestions={symbolSuggestions}
                    handleSuccess={async () => {
                      service.dispatchSnackbar(dispatch, 'Slave added successfully', 'alert', 'success', 5000);
                      if (copyEx) {
                        await copyExistingTrade(selectedSlaveId, selectedMasterId, selectedSlaveAccountId);
                      }
                      handleSuccess();
                      handleClose();
                    }}
                  />
                )}
              </StepWrapper>
            </>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default AddSlaveForm;
